/**
 * Offerte Generator — interactieve, chat-gestuurde opbouw van een CONCEPT-offerte.
 * Start vanuit een e-mail of gespreksnotities; de assistent vraagt door over wat
 * ontbreekt (verzint niets) en levert, zodra 'ie genoeg weet, het concept op:
 * omschrijving (vaste opbouw) + offerteregels met richtprijzen + prijsconsistentie-
 * check vs historie. Daarna optioneel als concept-offerte naar Gripp.
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { offerteNummersUitTekst } from './referentieNummers';
import VersiesDrawer from './VersiesDrawer';
import { gesprekWeergave, gesprekMatcht, begrensBreedte, leesGesprekkenBreedte, bewaarGesprekkenBreedte, GESPREKKEN_BREEDTE } from './gesprekTitel';
import {
  Box, Paper, Typography, TextField, Button, Autocomplete, CircularProgress, Alert, Divider, Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, IconButton as MuiIconButton, Menu, useMediaQuery, LinearProgress,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import CloseIcon from '@mui/icons-material/Close';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import BusinessIcon from '@mui/icons-material/Business';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { useMsal } from '@azure/msal-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { bevestig } from '../../utils/bevestig';
import { startActivity } from '../../utils/activityTracker';
import { searchOnenote, getOnenotePageText } from '../../utils/onenoteSearch';
import { searchMail, getMailBody, getConversationText, groepeerPerGesprek } from '../../utils/graphMailSearch';
import { buildGrippOfferUrl } from '../../utils/grippDeepLink';
import KlantAfspraken from './KlantAfspraken';
import EditNoteIcon from '@mui/icons-material/EditNote';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PageHeader from '../../ui-components/PageHeader';
import OfferteEmailButton from './OfferteEmailButton';
import { downloadOffertePdf } from './offertePdf';
import PresentatieKnop from './PresentatieKnop';
import { postMetBtwControle } from './btwKeuze';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OfferteDocument from './OfferteDocument';
import BasisoffertesDialog from './BasisoffertesDialog';
import { normaliseer as normaliseerRegels } from './regelBewerking';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import OfferteStatusRegel from './OfferteStatusRegel';
import OfferteEigenschappen from './OfferteEigenschappen';
import OffertePreflight from './OffertePreflight';
import AssistentBalk from './AssistentBalk';
import { externeMailadressen } from './offerteNummers';
import { nextAction, missingGrippFields } from './offerteFlow';
import { ACCENT, GRIPP_FIELD_OPTIONS, linkifyText } from './offerteUi';
import Link from '@mui/material/Link';

// Het eerste chat-bericht draagt de brontekst. Die prefix is ook de enige manier om de
// bron terug te winnen bij het heropenen van een gesprek — de bron zelf heeft geen eigen
// kolom in offerte_chats, en zonder terugwinnen draait de offerte-check met een lege bron.
const SOURCE_INTRO_PREFIX = 'Hier is de bron (e-mail / gespreksnotities) voor de offerte:\n\n';

// Taal van het concept — bepaalt of de backend Engelse Gripp-producten opzoekt
// (de REGELTITEL in de offerte is de productnaam, dus "Regisseur" blijft anders
// Nederlands ook al is de omschrijving vertaald). Detectie op stopwoorden in de
// omschrijving + regelnamen; de gebruiker kan het altijd overrulen.
const NL_STOPWORDS = ['de', 'het', 'een', 'en', 'van', 'voor', 'met', 'wordt', 'we', 'je', 'die', 'dat', 'aan', 'ook', 'niet', 'bij'];
const EN_STOPWORDS = ['the', 'and', 'of', 'for', 'with', 'will', 'you', 'this', 'that', 'are', 'from', 'your', 'shoot', 'day'];
function detectDraftLanguage(draft) {
  const text = [draft?.description || '', ...(draft?.lines || []).map((l) => `${l?.description || ''} ${l?.detail || ''}`)]
    .join(' ').toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/).filter(Boolean);
  if (text.length < 15) return 'nl';
  const nl = text.filter((w) => NL_STOPWORDS.includes(w)).length;
  const en = text.filter((w) => EN_STOPWORDS.includes(w)).length;
  return en > nl ? 'en' : 'nl';
}

// Vaste, logische sectie-volgorde voor de offerteregels (video én animatie). Onbekende
// secties belanden net vóór 'Algemene kosten'. 'Post' vóór 'Producti' checken, anders
// matcht "Post productie" op 'producti'.
function sectionRank(s) {
  const n = (s || '').toLowerCase().trim();
  if (!n) return 97;
  if (n.startsWith('pre')) return 0;
  if (n.includes('script')) return 1;
  if (n.includes('storyboard')) return 2;
  if (n.startsWith('post') || n.includes('afwerk')) return 6;
  if (n.includes('animati')) return 4;
  if (n.includes('thumbnail')) return 5;
  if (n.includes('producti')) return 3;
  if (n.includes('algemene') || n.includes('overige')) return 98;
  return 96;
}
// Stabiele sortering (index als tiebreak) zodat de onderlinge volgorde binnen een sectie blijft.
function sortBySection(lines) {
  return (lines || [])
    .map((l, i) => [l, i])
    .sort((a, b) => (sectionRank(a[0].section) - sectionRank(b[0].section)) || (a[1] - b[1]))
    .map(([l]) => l);
}

// Sectie-kaart met icoon-header (huisstijl, zoals Materiaalreservering) — geeft de
// pagina duidelijke kopjes, kleur en hiërarchie i.p.v. losse velden onder elkaar.
function SectionCard({ icon, title, subtitle, color = ACCENT.main, softBg = ACCENT.soft, children, sx }) {
  return (
    <Paper elevation={0} sx={{ mb: 2, border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', ...sx }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.1, bgcolor: softBg, borderBottom: '1px solid #e5e7eb' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: color, color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.2, color: '#1f2937' }}>
            {title}
          </Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
      </Stack>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

// Uitslag van de AI-offertecheck (gedeeld door de concept-flow én de losse check).
function CheckResultView({ result }) {
  if (!result) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      {result.samenvatting && <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{result.samenvatting}</Typography>}
      <Stack spacing={0.75}>
        {(result.checks || []).map((c, i) => {
          const st = String(c.status || '').toLowerCase();
          const color = st === 'ok' ? '#16a34a' : st === 'fout' ? '#dc2626' : '#d97706';
          const icon = st === 'ok' ? '✅' : st === 'fout' ? '❌' : '⚠️';
          return (
            <Box key={i} sx={{ display: 'flex', gap: 1 }}>
              <Box component="span" sx={{ flexShrink: 0 }}>{icon}</Box>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color }}>{c.titel}</Typography>
                {c.toelichting && <Typography variant="caption" color="text.secondary">{c.toelichting}</Typography>}
              </Box>
            </Box>
          );
        })}
      </Stack>
      {Array.isArray(result.suggesties) && result.suggesties.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <Typography variant="caption" fontWeight={800} sx={{ color: '#1d4ed8', display: 'block', mb: 0.5, letterSpacing: '0.03em' }}>
            💡 SUGGESTIES (direct doorvoerbaar)
          </Typography>
          <Stack spacing={0.75}>
            {result.suggesties.map((s, i) => (
              <Box key={i}>
                {s.plek && <Typography variant="caption" fontWeight={700} sx={{ color: '#1e3a8a' }}>{s.plek}</Typography>}
                <Typography variant="body2" sx={{ color: '#334155' }}>
                  {s.van ? <><Box component="span" sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>{s.van}</Box>{' → '}</> : null}
                  <Box component="span" fontWeight={600}>{s.naar}</Box>
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// Voortgangs-venster tijdens de (trage) AI-check: loopt de controles langs zodat
// zichtbaar is dat er iets gebeurt.
const CHECK_STEPS = [
  'Offerte + regels laden uit Gripp…',
  'Logica en incl./excl. nalopen…',
  'Deliverables & standaard opbouw checken…',
  'Tarieven vergelijken met het huistarief…',
  'Projectbegeleiding-percentage berekenen…',
  'BTW & klant-afspraken controleren…',
  'Vorige / soortgelijke offertes vergelijken…',
  'Spelling en netheid nalopen…',
];
function CheckProgress({ active }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!active) { setI(0); return undefined; }
    const t = setInterval(() => setI(v => Math.min(v + 1, CHECK_STEPS.length - 1)), 2400);
    return () => clearInterval(t);
  }, [active]);
  if (!active) return null;
  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <CircularProgress size={16} sx={{ color: '#d97706' }} />
        <Typography variant="body2" fontWeight={700} sx={{ color: '#92400e' }}>{CHECK_STEPS[i]}</Typography>
      </Stack>
      <Stack spacing={0.25}>
        {CHECK_STEPS.map((s, k) => (
          <Stack key={k} direction="row" spacing={0.75} alignItems="center">
            <Box component="span" sx={{ fontSize: 12, width: 16, textAlign: 'center' }}>{k < i ? '✅' : k === i ? '⏳' : '•'}</Box>
            <Typography variant="caption" sx={{ color: k <= i ? '#78350f' : '#a8a29e' }}>{s}</Typography>
          </Stack>
        ))}
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#a16207' }}>Dit kan ~20–40 sec duren — de AI leest de hele offerte na.</Typography>
    </Box>
  );
}

// Ingeklapte kolom: smalle klikbare balk om 'm weer open te klappen.

// Klikbare kaart-kop met chevron — voor de in-/uitklapbare secties in de offerte-pane.

// Detecteer meerdere offertes in één omschrijving: delen gescheiden door ---
// met elk een 'Offerte N'-kop. De assistent bouwt zo'n gecombineerd document
// omdat één gesprek maar één draft kent — deze helper splitst het weer.
function splitsOffertes(description) {
  const tekst = String(description || '');
  const delen = tekst.split(/\r?\n[ \t]*-{3,}[ \t]*\r?\n/).map((d) => d.trim()).filter(Boolean);
  if (delen.length < 2) return null;
  const kopRe = /^#{1,3}\s*Offerte\s*\d+\s*[–—-]?\s*(.*)$/im;
  // Minstens één deel moet expliciet een 'Offerte N'-kop hebben, anders is
  // de --- gewoon een scheidingslijn in een normale omschrijving.
  if (!delen.some((d) => kopRe.test(d))) return null;
  return delen.map((d, i) => {
    const m = d.match(kopRe);
    const subject = (m && m[1] ? m[1].trim() : '') || `Offerte ${i + 1}`;
    // De 'Offerte N'-kopregel zelf eruit — de titel staat straks boven het document.
    const body = d.replace(kopRe, '').replace(/^\n+/, '').trim();
    return { subject, description: body, hadKop: !!m };
  });
}

// Maak URL's in chat-tekst klikbaar (o.a. Frame.io review-links).

// Standaard omzetverdeling voor een níeuw concept: volgende maand t/m de maand
// erna — hetzelfde venster dat de backend voorheen stilzwijgend invulde.
const maandIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const omzetDefaults = () => {
  const nu = new Date();
  return {
    omzetStart: maandIso(new Date(nu.getFullYear(), nu.getMonth() + 1, 1)),
    omzetEind: maandIso(new Date(nu.getFullYear(), nu.getMonth() + 2, 1)),
  };
};

export default function OffertesGenerator() {
  const { instance } = useMsal();
  const userEmail = instance.getActiveAccount()?.username || '';
  const location = useLocation();

  // Bron
  const [mail, setMail] = useState('');
  // Spraakherkenning (Web Speech API): alleen tonen als de browser het kan.
  const SpraakHerkenning = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const spraakBeschikbaar = !!SpraakHerkenning;
  const [luistert, setLuistert] = useState(false);
  const herkenningRef = useRef(null);
  const startLuisteren = () => {
    if (!SpraakHerkenning) return;
    const r = new SpraakHerkenning();
    r.lang = 'nl-NL'; r.continuous = true; r.interimResults = false;
    r.onresult = (e) => {
      const stuk = Array.from(e.results).slice(e.resultIndex).map((x) => x[0]?.transcript || '').join(' ').trim();
      if (stuk) setMail((prev) => `${prev ? `${prev.trimEnd()} ` : ''}${stuk}`);
    };
    r.onerror = () => setLuistert(false);
    r.onend = () => setLuistert(false);
    herkenningRef.current = r; r.start(); setLuistert(true);
  };
  const stopLuisteren = () => { try { herkenningRef.current?.stop(); } catch (_) { /* al gestopt */ } setLuistert(false); };
  const [attachBusy, setAttachBusy] = useState(false);
  const attachInputRef = useRef(null);

  // Gekoppelde bronnen — onthouden per offerte (PDF's, meetings, OneNote, e-mail) zodat
  // ze bij de hele offerte blijven horen en na heropenen terugkomen (persist via saveChat).
  const [sources, setSources] = useState([]);
  // Achtergrond-kaart (bronnen) ook ná het starten tonen, bv. bij een geladen Gripp-offerte.
  const [achtergrondOpen, setAchtergrondOpen] = useState(false);
  useEffect(() => { if (achtergrondOpen) window.scrollTo({ top: 0, behavior: 'smooth' }); }, [achtergrondOpen]);
  const addTrackedSource = (s) => setSources((prev) =>
    prev.some((p) => p.id === s.id) ? prev : [...prev, { ...s, addedAt: new Date().toISOString() }]);
  const removeTrackedSource = (id) => setSources((prev) => prev.filter((p) => p.id !== id));

  // 📎 Bijlage(n) als bron: PDF/Word/tekst → tekst extraheren → bij het bron-veld.
  const addAttachments = async (files) => {
    if (!files?.length) return;
    setAttachBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await authFetch(`${BACKEND_URL}/Api/parse-attachment`, { method: 'POST', body: fd });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || `${f.name} lezen mislukt`);
        if (!(d.text || '').trim()) throw new Error(`${f.name}: geen tekst gevonden (gescande PDF?)`);
        setMail((prev) => [prev.trim(), `--- Bijlage: ${d.filename} ---\n${d.text}`].filter(Boolean).join('\n\n'));
        addTrackedSource({ id: `file-${d.filename}`, kind: 'bijlage', title: d.filename, sub: `${(d.text || '').length.toLocaleString('nl-NL')} tekens`, url: null });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setAttachBusy(false);
      if (attachInputRef.current) attachInputRef.current.value = '';
    }
  };
  const [meetings, setMeetings] = useState([]);
  const [loadingMeeting, setLoadingMeeting] = useState(false);
  // Gecombineerd bronnen-zoeken (OneNote-notulen + eigen e-mail) in één veld.
  // Resultaten worden samengevoegd met een bron-label; "+ toevoegen" plakt de
  // volledige tekst in het bron-veld.
  const [srcQuery, setSrcQuery] = useState('');
  const [sourceTerms, setSourceTerms] = useState([]);   // gebruikte zoektermen → voor een heldere gesprekstitel
  const [srcResults, setSrcResults] = useState(null);   // null = nog niet gezocht
  const [srcLoading, setSrcLoading] = useState(false);
  const [srcError, setSrcError] = useState(null);
  const [srcAdding, setSrcAdding] = useState(null);     // id van item dat toegevoegd wordt
  const [openGesprekken, setOpenGesprekken] = useState({}); // gesprek-id -> losse berichten tonen
  const [srcPreview, setSrcPreview] = useState(null);   // { open, loading, title, sub, body, url } — mail/notulen inkijken vóór toevoegen

  // Zelfde opschoning als Frits' mail-preview: Graph's platte tekst laat links
  // achter als "Tekst<https://url>" en afbeeldingen als "[https://...png]".
  const renderBronTekst = (text) => {
    const cleaned = String(text || '')
      .replace(/\[(https?:\/\/[^\]]*\.(?:png|jpe?g|gif|webp|svg)[^\]]*)\]/gi, '')
      .replace(/\[cid:[^\]]+\]/gi, '')
      .replace(/\n{3,}/g, '\n\n');
    const parts = cleaned.split(/(<https?:\/\/[^>\s]+>|https?:\/\/[^\s<>\])]+)/g);
    return parts.map((part, i) => {
      let url = null;
      if (/^<https?:\/\//.test(part)) url = part.slice(1, -1);
      else if (/^https?:\/\//.test(part)) url = part;
      if (!url) return part;
      let label = 'link';
      try { label = new URL(url).hostname.replace(/^www\./, ''); } catch { /* hou 'link' */ }
      return <Link key={i} href={url} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{label}&nbsp;↗</Link>;
    });
  };

  const openBronPreview = async (item) => {
    setSrcPreview({ open: true, loading: true, title: item.title, sub: item.sub, url: item.url, item });
    let body = '';
    try {
      if (item.kind === 'thread') {
        body = (await getConversationText(item.convId, { maxChars: 6000 })).tekst;
      } else {
        body = item.source === 'onenote'
          ? await getOnenotePageText(item.refId)
          : await getMailBody(item.refId);
      }
    } catch (err) {
      body = `Kon de inhoud niet ophalen: ${err.message}`;
    }
    // Alleen bijwerken als de preview nog openstaat (trage fetch na Esc).
    setSrcPreview((prev) => (prev?.open ? { ...prev, loading: false, body } : prev));
  };

  // Bron aangeleverd via navigatie (bv. Mail-assistent: "Start offerte uit deze mail").
  useEffect(() => {
    const s = location.state;
    if (s?.sourceText) {
      setMail(s.sourceText);
      if (s.sourceLabel) setSourceTerms([s.sourceLabel]);
      window.history.replaceState({}, ''); // niet opnieuw voorvullen bij refresh
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Meeting-notulen doorzoeken (op inhoud) via de AI-engine.
  const searchMeetings = async (q) => {
    const url = `${AI_ENGINE_URL}/api/v1/meetings/?search=${encodeURIComponent(q)}${userEmail ? `&user_email=${encodeURIComponent(userEmail)}` : ''}`;
    const res = await authFetch(url);
    if (!res.ok) throw new Error(`meetings ${res.status}`);
    const d = await res.json();
    return Array.isArray(d) ? d : (d.meetings || d.results || []);
  };
  const meetingTitle = (m) => m.title || m.subject || m.name || (m.date ? `Meeting ${String(m.date).slice(0, 10)}` : `Meeting ${m.id}`);
  const runSourceSearch = async () => {
    const q = srcQuery.trim();
    if (!q) return;
    setSourceTerms(prev => (prev.includes(q) ? prev : [...prev, q].slice(-5)));
    setSrcLoading(true); setSrcError(null); setSrcResults(null);
    // Drie bronnen parallel; faalt er één (bv. geen mail-toestemming) dan tonen we
    // de rest gewoon + een nette melding.
    const [note, mailRes, mtgRes] = await Promise.allSettled([searchOnenote(q), searchMail(q), searchMeetings(q)]);
    const items = [];
    const errs = [];
    if (mtgRes.status === 'fulfilled') {
      (mtgRes.value || []).slice(0, 15).forEach(m => items.push({ source: 'meeting', id: `mtg-${m.id}`, refId: m.id, title: meetingTitle(m), sub: m.date ? String(m.date).slice(0, 10) : '', url: null }));
    } else errs.push(`Meetings: ${mtgRes.reason?.message || 'mislukt'}`);
    if (note.status === 'fulfilled') {
      note.value.forEach(p => items.push({ source: 'onenote', id: `on-${p.id}`, refId: p.id, title: p.title, sub: p.modified ? String(p.modified).slice(0, 10) : '', url: p.webUrl }));
    } else errs.push(`OneNote: ${note.reason?.message || 'mislukt'}`);
    if (mailRes.status === 'fulfilled') {
      // Per gesprek één rij: een thread levert losse "RE:"/"FW:"-treffers op, en
      // als bron wil je de hele conversatie — niet de flarden.
      const berichtItem = (m) => ({ source: 'email', id: `mail-${m.id}`, refId: m.id, title: m.subject, sub: [m.from, m.received ? String(m.received).slice(0, 10) : ''].filter(Boolean).join(' · '), url: m.webLink, subject: m.subject, from: m.from });
      groepeerPerGesprek(mailRes.value).forEach(groep => {
        const kids = groep.berichten.map(berichtItem);
        if (!groep.conversationId) { items.push(kids[0]); return; }
        const deelnemers = groep.deelnemers;
        items.push({
          source: 'email', kind: 'thread', convId: groep.conversationId,
          id: `thr-${groep.conversationId}`, title: groep.onderwerp, subject: groep.onderwerp,
          url: groep.laatste?.webLink || null,
          sub: [
            deelnemers.slice(0, 3).join(', ') + (deelnemers.length > 3 ? ` +${deelnemers.length - 3}` : ''),
            groep.laatste?.received ? String(groep.laatste.received).slice(0, 10) : '',
            `${kids.length} treffer${kids.length === 1 ? '' : 's'} — hele gesprek toevoegen`,
          ].filter(Boolean).join(' · '),
          kids,
        });
      });
    } else errs.push(`E-mail: ${mailRes.reason?.message || 'mislukt'}`);
    setSrcResults(items);
    setSrcError(errs.length ? errs.join(' · ') : null);
    setSrcLoading(false);
  };
  const addSourceResult = async (item) => {
    setSrcAdding(item.id); setSrcError(null);
    try {
      let block;
      if (item.source === 'onenote') {
        const text = await getOnenotePageText(item.refId);
        block = `\n\n— Uit OneNote-notulen: ${item.title} —\n${text}`.slice(0, 8000);
      } else if (item.source === 'meeting') {
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${item.refId}`);
        const full = res.ok ? await res.json() : {};
        const text = [full.summary, full.transcript].filter(Boolean).join('\n\n').slice(0, 12000);
        block = `\n\n— Uit meeting-notulen: ${item.title} —\n${text}`;
      } else if (item.kind === 'thread') {
        const { aantal, tekst } = await getConversationText(item.convId);
        block = `\n\n— Uit e-mailgesprek: ${item.subject} (${aantal} bericht${aantal === 1 ? '' : 'en'}, chronologisch) —\n${tekst}`;
      } else {
        const text = await getMailBody(item.refId);
        block = `\n\n— Uit e-mail: ${item.subject}${item.from ? ` (van ${item.from})` : ''} —\n${text}`.slice(0, 8000);
      }
      setMail(prev => (prev ? prev + block : block.trim()));
      addTrackedSource({ id: item.id, kind: item.source, title: item.title, sub: item.sub || '', url: item.url || null });
      // Toegevoegde bron uit de zoekresultaten halen zodat je 'm niet dubbel toevoegt
      // — ook als het een los bericht binnen een gesprek is.
      setSrcResults(prev => (prev || [])
        .filter(r => r.id !== item.id)
        .map(r => (r.kids ? { ...r, kids: r.kids.filter(k => k.id !== item.id) } : r))
        .filter(r => !r.kids || r.kids.length));
    } catch (err) {
      setSrcError(err.message);
    } finally { setSrcAdding(null); }
  };

  // Chat
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', content}
  // Het chatveld houdt zijn tekst ZELF bij (AssistentBalk): stond de invoer hier,
  // dan rendeerde elke toetsaanslag de hele generator (incl. dnd-kit-regels) opnieuw
  // en werd typen traag (30 okt). Van buitenaf zetten/legen kan via deze prefill;
  // `n` ophogen = de balk neemt `tekst` over. chatInvoerRef spiegelt de actuele
  // tekst zonder te renderen, zodat een remount van de balk (started-cyclus) de
  // getypte tekst terugzet zoals toen de state hier stond.
  const chatInvoerRef = useRef('');
  const [chatPrefill, setChatPrefill] = useState({ tekst: '', n: 0 });
  const zetChatInvoer = useCallback((tekst) => {
    chatInvoerRef.current = tekst;
    setChatPrefill((p) => ({ tekst, n: p.n + 1 }));
  }, []);
  const [chatLoading, setChatLoading] = useState(false);
  const [started, setStarted] = useState(false);
  // Gesprekken-kolom: standaard automatisch (open bij de start, dicht zodra een gesprek
  // loopt — dan is alle breedte voor chat + concept). null = automatisch, true/false = keuze.
  const [chatsOverride, setChatsOverride] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null); // {number,id} als een bestaande Gripp-offerte geladen is
  const [loadDlg, setLoadDlg] = useState(null); // { q, loading } — dialoog 'bestaande offerte openen'
  const [beheerAnchor, setBeheerAnchor] = useState(null); // beheer-menu (declutter header)
  // Gripp-basisoffertes: bron van waarheid voor opbouw en prijzen (Beheer → Basisoffertes).
  const [basisOpen, setBasisOpen] = useState(false);
  const [basisOffers, setBasisOffers] = useState(null);
  const [basisCatalogus, setBasisCatalogus] = useState(null);
  // Ctrl+Z / Cmd+Z = laatste herordening of verwijdering in de regels terugdraaien.
  useEffect(() => registreerOngedaanSneltoets(), []);
  const threadEndRef = useRef(null);

  // Concept + checks
  const [draft, setDraft] = useState(null);
  const [priceCheck, setPriceCheck] = useState([]);
  const [checkResult, setCheckResult] = useState(null);   // AI-offerte-check
  const [checkLoading, setCheckLoading] = useState(false);
  const [error, setError] = useState(null);

  // Gripp
  const [company, setCompany] = useState(null);
  // Btw-tarief van de klant voor de totalen: komt de klant niet uit de klantzoeker (offerte
  // geladen, via contactpersoon), haal het dan op klant-id op — anders rekent het scherm 21%
  // waar Gripp het klanttarief (bv. BTW Intern 0%) gebruikt. Eén poging per klant.
  // Opgehaalde tarieven per klant-id (ook voor een klant die later opnieuw zonder vat binnenkomt).
  const btwPerKlantRef = useRef(new Map());
  const btwBezigRef = useRef(new Set());
  useEffect(() => {
    const id = company?.id;
    if (!id || company.vat !== undefined) return undefined;
    const sleutel = String(id);
    if (btwPerKlantRef.current.has(sleutel)) {
      const vat = btwPerKlantRef.current.get(sleutel);
      setCompany((c) => (c && c.id === id && c.vat === undefined ? { ...c, vat } : c));
      return undefined;
    }
    if (btwBezigRef.current.has(sleutel)) return undefined;
    btwBezigRef.current.add(sleutel);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/offertes/company-vat?id=${encodeURIComponent(id)}`);
        const d = await r.json();
        if (r.ok) {
          const vat = d.vat ?? null;
          btwPerKlantRef.current.set(sleutel, vat);
          setCompany((c) => (c && String(c.id) === sleutel && c.vat === undefined ? { ...c, vat } : c));
        }
      } catch { /* totalen vallen terug op het tarief van de regels */ }
      btwBezigRef.current.delete(sleutel);
    })();
    return undefined;
  }, [company?.id, company?.vat]); // eslint-disable-line react-hooks/exhaustive-deps
  const [companyOpts, setCompanyOpts] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyQuery, setCompanyQuery] = useState(''); // laatst getypte zoekterm — voor de ↻-knop
  // 'gripp' = live, 'spiegel' = Gripp onbereikbaar, klanten uit de Airtable-spiegel
  const [companySource, setCompanySource] = useState('gripp');
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const seqRef = useRef(0);
  const debounceRef = useRef(null);

  // Opgeslagen offerte-gesprekken
  const [savedChats, setSavedChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Modus: nieuwe offerte (chat) of omschrijving bijwerken (bestaande Gripp-offerte).
  // Kan via URL voorgezet worden (bv. Frits → ?mode=aanpassen&number=9572).
  const [searchParams] = useSearchParams();
  const VALID_MODES = ['new', 'check']; // split-view (nieuw + bestaand) óf losse offerte-controle
  // 'aanpassen' is de historische naam die Frits nog stuurt (MailAssistent.jsx) en die
  // hoort bij de split-view: ?number= laadt de bestaande offerte er meteen in.
  const MODE_ALIASES = { aanpassen: 'new', bijwerken: 'new' };
  const urlMode = searchParams.get('mode');
  const requestedMode = MODE_ALIASES[urlMode] || urlMode;
  const [mode, setMode] = useState(VALID_MODES.includes(requestedMode) ? requestedMode : 'new');
  const initialOfferNumber = searchParams.get('number') || '';

  // Vanuit de projectflow geopend (?project=…&from=flow): projectinfo,
  // kern-briefing en klantpaspoort als bron voorvullen, zodat de assistent
  // dezelfde context heeft als het project — niet blanco beginnen.
  const flowProjectId = searchParams.get('project') || '';
  const flowContextGeladen = useRef(false);
  // Wat er uit het project is meegekomen — tonen we bovenaan, zodat je ziet dat
  // je niet blanco begint én wat er in het project nog leeg is.
  const [flowContext, setFlowContext] = useState(null); // {project, briefingVelden, paspoortVelden, klantGekoppeld, laden, fout}
  useEffect(() => {
    if (!flowProjectId || flowContextGeladen.current) return;
    flowContextGeladen.current = true;
    setFlowContext({ laden: true });
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${encodeURIComponent(flowProjectId)}/context`);
        if (!r.ok) throw new Error('projectcontext niet gelukt');
        const { project, briefing, passport } = await r.json();
        const regels = [];
        regels.push(`[PROJECTCONTEXT — ${project.number ? `project ${project.number} · ` : ''}${project.name || ''}${project.client ? ` · klant: ${project.client}` : ''}]`);
        const vul = (kop, obj, velden) => {
          const rows = (velden || Object.keys(obj || {}))
            .filter(k => obj?.[k])
            .map(k => `- ${k}: ${Array.isArray(obj[k]) ? obj[k].join(', ') : obj[k]}`);
          if (rows.length) regels.push(`\n${kop}:\n${rows.join('\n')}`);
          return rows.length;
        };
        const paspoortVelden = vul('KLANTPASPOORT (tone-of-voice en merk)', passport);
        const briefingVelden = vul('BRIEFING', briefing, ['Doel en Kernboodschap', 'Doelgroep', 'Stijl', 'Aanpak', 'Advies', 'Inzet', 'Deliverables (Opmerkingen)', 'Budgetindicatie', 'Opleverdatum']);
        // Altijd voorvullen — ook als alleen de kopregel gevuld is. Het projectnummer
        // en de klantnaam zijn precies wat de assistent anders opnieuw moet vragen.
        const blok = regels.join('\n');
        setMail(prev => (prev ? `${blok}\n\n---\n\n${prev}` : blok));

        // Klant meteen kóppelen (niet alleen opzoeken): exacte naamtreffer in Gripp
        // selecteren, zodat prijsafspraken en contactpersonen vanzelf laden.
        let klantGekoppeld = false;
        if (project.client) {
          searchCompanies(project.client);
          try {
            const cr = await authFetch(`${BACKEND_URL}/Api/offertes/companies?q=${encodeURIComponent(project.client)}`);
            const lijst = (await leesJson(cr, 'Klant zoeken')).companies || [];
            const norm = (s) => String(s || '').toLowerCase().replace(/\s+|b\.?v\.?$/g, '');
            const treffer = lijst.find((c) => norm(c.name) === norm(project.client))
              || (lijst.length === 1 ? lijst[0] : null);
            if (treffer) {
              setCompany(treffer); fetchAgreement(treffer); loadContacts(treffer);
              klantGekoppeld = true;
            }
          } catch { /* bewust stil: klant koppelen is optioneel — searchCompanies() hierboven meldt een zoekfout al */ }
        }
        setFlowContext({ project, briefingVelden, paspoortVelden, klantGekoppeld });
        toast.success(`Context van ${project.number ? `project ${project.number}` : 'het project'} geladen`);
      } catch (e) {
        setFlowContext({ fout: e.message || 'projectcontext niet gelukt' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowProjectId]);

  // Klant-prijsafspraken
  const [clientAgreement, setClientAgreement] = useState(null); // {company_name, agreements,...} of null

  // Oude offertes als referentie (live uit Gripp) om mee te vergelijken bij een nieuwe.
  const [refOpts, setRefOpts] = useState([]);
  const [refLoading, setRefLoading] = useState(false);
  const [references, setReferences] = useState([]); // [{number, subject, company, description, linesSummary}]
  const refSeq = useRef(0);
  const refDebounce = useRef(null);
  const searchOffers = useCallback((q) => {
    if (refDebounce.current) clearTimeout(refDebounce.current);
    refDebounce.current = setTimeout(async () => {
      if (!q || q.length < 2) { setRefOpts([]); return; }
      const seq = ++refSeq.current;
      setRefLoading(true);
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/offertes/search-offers?q=${encodeURIComponent(q)}`);
        const d = await leesJson(res, 'Offertes zoeken');
        if (seq === refSeq.current) setRefOpts(d.offers || []);
      } catch (e) { meldFout(e, 'Offertes zoeken'); } finally { if (seq === refSeq.current) setRefLoading(false); }
    }, 300);
  }, []);
  const laadReferentie = useCallback(async (off) => {
    const res = await authFetch(`${BACKEND_URL}/Api/offertes/offer?number=${encodeURIComponent(off.number)}`);
    const d = await res.json();
    if (!res.ok) throw new Error(d.error || 'Offerte laden mislukt');
    return {
      id: off.id ?? d.offer.id ?? null,          // voor de "open in Gripp"-deeplink
      number: d.offer.number, subject: d.offer.subject,
      company: off.company || '', description: d.offer.description || '', linesSummary: d.linesSummary || '',
    };
  }, []);
  const addReference = useCallback(async (off) => {
    if (!off?.number || references.some(r => r.number === off.number)) return;
    try {
      const ref = await laadReferentie(off);
      setReferences(prev => (prev.some(r => r.number === ref.number) ? prev : [...prev, ref]));
    } catch (err) { setError(err.message); }
  }, [references, laadReferentie]);
  // Noem je in de chat een offerte ("houd offerte 9756 aan"), dan wordt die automatisch
  // referentie — ook voor de offerteregels. 4 nov: de tekst gebruikte 9756 wel, maar de
  // regels kwamen uit de basisofferte (90 s) en werden ×5,3 en ×8 opgeschaald.
  const referencesRef = useRef(references);
  useEffect(() => { referencesRef.current = references; }, [references]);
  const referentiesUitChat = useCallback(async (tekst) => {
    const heeft = (n) => referencesRef.current.some((r) => String(Number(r.number)) === n);
    const nummers = [...new Set(offerteNummersUitTekst(tekst))].filter((n) => !heeft(n));
    if (!nummers.length) return referencesRef.current;
    const nieuw = [];
    const mislukt = [];
    for (const number of nummers.slice(0, 3)) {
      try {
        const ref = await laadReferentie({ number });
        if (!heeft(String(Number(ref.number))) && !nieuw.some((x) => x.number === ref.number)) nieuw.push(ref);
      } catch { mislukt.push(number); }
    }
    // Niet stil: zonder referentie rekenen de regels met de basisofferte — dat is precies wat misging.
    if (mislukt.length) toast.error(`Offerte ${mislukt.join(', ')} kon niet als referentie geladen worden — koppel hem via 'Oude offerte erbij pakken'`);
    if (!nieuw.length) return referencesRef.current;
    const samen = [...referencesRef.current, ...nieuw];
    referencesRef.current = samen;
    setReferences(samen);
    toast.success(`Referentie-offerte ${nieuw.map((n) => n.number).join(', ')} gekoppeld (uit je bericht)`);
    return samen;
  }, [laadReferentie]);
  const removeReference = useCallback((number) => setReferences(prev => prev.filter(r => r.number !== number)), []);
  // Bewaarde referenties na heropenen verversen: de opgeslagen linesSummary kan
  // verouderd zijn (bv. van vóór de stukprijzen-fix) of de offerte is aangepast.
  const versReferenties = useCallback(async (lijst) => {
    const uit = [];
    for (const r of lijst) {
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/offertes/offer?number=${encodeURIComponent(r.number)}`);
        const d = await res.json();
        if (res.ok) { uit.push({ ...r, description: d.offer?.description || r.description, linesSummary: d.linesSummary || r.linesSummary }); continue; }
      } catch { /* hou de bewaarde versie */ }
      uit.push(r);
    }
    setReferences(uit);
  }, []);
  const [showAfspraken, setShowAfspraken] = useState(false);
  const [showKlantRef, setShowKlantRef] = useState(false);  // optionele klant/referentie-sectie in-/uitklappen
  const [chatSearch, setChatSearch] = useState('');         // zoeken in eerdere gesprekken

  // Medewerker ("namens" → accountmanager) + contactpersoon bij de klant
  const [employees, setEmployees] = useState([]);
  const [accountManager, setAccountManager] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contact, setContact] = useState(null);

  useEffect(() => {
    const url = `${BACKEND_URL}/Api/offertes/employees${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ''}`;
    authFetch(url).then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      setEmployees(d.employees || []);
      if (d.matchId) setAccountManager((d.employees || []).find(e => e.id === d.matchId) || null);
    }).catch(() => {});
  }, [userEmail]);

  // Contactpersonen van de gekozen klant laden (auto-select bij precies één).
  // Ook als ververs-knop: net in Gripp aangemaakte contactpersonen direct erbij,
  // met behoud van de huidige selectie als die nog bestaat.
  const [contactsLoading, setContactsLoading] = useState(false);
  // Gripp-productcatalogus voor de omschrijving-dropdown op offerteregels
  const [products, setProducts] = useState(null);
  const loadProducts = useCallback(async () => {
    if (products) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/offertes/products`);
      const d = await leesJson(r, 'Producten laden');
      setProducts(d.products || []);
    } catch (e) { meldFout(e, 'Producten laden'); setProducts([]); }
  }, [products]);
  const loadContacts = useCallback(async (comp) => {
    if (!comp?.id) { setContacts([]); setContact(null); return; }
    setContactsLoading(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/contacts?companyId=${encodeURIComponent(comp.id)}`);
      const d = await leesJson(res, 'Contactpersonen laden');
      const list = d.contacts || [];
      setContacts(list);
      // Auto-selecteren op de afzender/contactpersoon uit de bron-mail: match op
      // e-mailadres (staat in de handtekening) of volledige naam. Anders: enige
      // contact, anders leeg. Bewaart een handmatige keuze.
      const src = (mail || '').toLowerCase();
      const bySrc = src
        ? list.find((c) => c.email && src.includes(c.email.toLowerCase()))
          || list.find((c) => c.name && c.name.length > 4 && src.includes(c.name.toLowerCase()))
        : null;
      setContact((cur) => (cur && list.some((c) => c.id === cur.id)) ? cur
        : (bySrc || (list.length === 1 ? list[0] : null)));
    } catch (e) { meldFout(e, 'Contactpersonen laden'); setContacts([]); setContact(null); }
    finally { setContactsLoading(false); }
  }, [mail]);

  // Contactpersoon zoeken over alle klanten heen; de klant volgt uit de contactpersoon
  // (wiki-feedback 26 sep: "je weet soms niet onder welke entiteit iemand staat").
  const [contactZoekOpts, setContactZoekOpts] = useState([]);
  const [contactZoekLoading, setContactZoekLoading] = useState(false);
  const [mailContact, setMailContact] = useState(null); // voorstel uit de bron-mail
  const contactZoekSeq = useRef(0);
  const contactZoekTimer = useRef(null);
  const zoekContactOveral = useCallback((q) => {
    if (contactZoekTimer.current) clearTimeout(contactZoekTimer.current);
    const seq = ++contactZoekSeq.current;
    const term = String(q || '').trim();
    if (term.length < 2) { setContactZoekOpts([]); setContactZoekLoading(false); return; }
    contactZoekTimer.current = setTimeout(async () => {
      setContactZoekLoading(true);
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/offertes/contacts/zoek?q=${encodeURIComponent(term)}`);
        const d = await leesJson(r, 'Contactpersoon zoeken');
        if (seq === contactZoekSeq.current) setContactZoekOpts(d.contacts || []);
      } catch (e) { if (seq === contactZoekSeq.current) { meldFout(e, 'Contactpersoon zoeken'); setContactZoekOpts([]); } }
      finally { if (seq === contactZoekSeq.current) setContactZoekLoading(false); }
    }, 300);
  }, []);

  // Voorstel uit de mail: externe e-mailadressen in de bron → contactpersoon in Gripp.
  useEffect(() => {
    if (company) { setMailContact(null); return undefined; }
    const adressen = externeMailadressen(mail).slice(0, 3);
    if (!adressen.length) { setMailContact(null); return undefined; }
    let weg = false;
    (async () => {
      for (const a of adressen) {
        try {
          const r = await authFetch(`${BACKEND_URL}/Api/offertes/contacts/zoek?q=${encodeURIComponent(a)}`);
          if (!r.ok) continue;
          const d = await r.json();
          const eerste = (d.contacts || [])[0];
          if (eerste) { if (!weg) setMailContact(eerste); return; }
        } catch { /* voorstel is een extraatje */ }
      }
      if (!weg) setMailContact(null);
    })();
    return () => { weg = true; };
  }, [mail, company]);

  // Haal de actieve prijsafspraak voor de gekozen klant op (voor prompt-injectie).
  const fetchAgreement = useCallback(async (comp) => {
    if (!comp) { setClientAgreement(null); return; }
    try {
      const params = comp.id
        ? `gripp_company_id=${encodeURIComponent(comp.id)}`
        : `company_name=${encodeURIComponent(comp.name || '')}`;
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/client-agreements/for-company?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setClientAgreement(d && d.agreements ? d : null);
    } catch {
      setClientAgreement(null);
      toast('Prijsafspraken van deze klant niet geladen — de offerte gebruikt standaardtarieven', { icon: '⚠️' }); // FU-08
    }
  }, []);

  const loadSavedList = useCallback(() => {
    const url = `${AI_ENGINE_URL}/api/v1/offerte-chats${userEmail ? `?user_email=${encodeURIComponent(userEmail)}` : ''}`;
    authFetch(url).then(r => r.ok ? r.json() : []).then(d => setSavedChats(Array.isArray(d) ? d : [])).catch(() => {});
  }, [userEmail]);
  useEffect(() => { loadSavedList(); }, [loadSavedList]);

  useEffect(() => {
    const url = `${AI_ENGINE_URL}/api/v1/meetings/${userEmail ? `?user_email=${encodeURIComponent(userEmail)}` : ''}`;
    authFetch(url).then(r => r.ok ? r.json() : []).then(d => setMeetings(Array.isArray(d) ? d : [])).catch(() => {});
  }, [userEmail]);

  useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, chatLoading]);

  const pickMeeting = async (m) => {
    if (!m) return;
    setLoadingMeeting(true);
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${m.id}`);
      const full = res.ok ? await res.json() : m;
      const text = [full.summary, full.transcript].filter(Boolean).join('\n\n').slice(0, 12000);
      if (text) setMail(text);
    } catch { /* stil */ } finally { setLoadingMeeting(false); }
  };

  // Bedrijven ophalen uit Gripp. Losse functie zodat de ↻-knop 'm direct (zonder
  // debounce) kan herhalen — bv. als je net een nieuwe relatie in Gripp hebt aangemaakt.
  const fetchCompanies = useCallback(async (q) => {
    const term = String(q || '').trim();
    if (term.length < 2) return;
    const seq = ++seqRef.current;
    setCompanyLoading(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/companies?q=${encodeURIComponent(term)}`);
      const data = await leesJson(res, 'Bedrijven zoeken');
      if (seq === seqRef.current) {
        setCompanyOpts(data.companies || []);
        setCompanySource(data.source || 'gripp');
      }
    } catch (e) { meldFout(e, 'Bedrijven zoeken'); } finally { if (seq === seqRef.current) setCompanyLoading(false); }
  }, []);

  const searchCompanies = useCallback((q) => {
    setCompanyQuery(q || '');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Elke inputwijziging maakt lopende requests ongeldig — anders kan een traag
    // antwoord de lijst later alsnog met oude treffers vullen (ook na leegmaken).
    seqRef.current += 1;
    if (String(q || '').trim().length < 2) { setCompanyOpts([]); setCompanyLoading(false); return; }
    debounceRef.current = setTimeout(() => { fetchCompanies(q); }, 300);
  }, [fetchCompanies]);

  // Expliciete ↻: debounce annuleren en direct opnieuw ophalen — met de getypte term,
  // of (als er niets getypt is) de naam van de al gekozen klant.
  const companyRefreshTerm = String(companyQuery || '').trim() || String(company?.name || '').trim();
  const refreshCompanies = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchCompanies(companyRefreshTerm);
  }, [fetchCompanies, companyRefreshTerm]);

  const prijsSeqRef = useRef(0);
  const runPriceCheck = useCallback(async (lines) => {
    const seq = ++prijsSeqRef.current; // een trage oude check mag een nieuwere niet overschrijven
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/price-check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines }),
      });
      const d = await res.json();
      if (res.ok && seq === prijsSeqRef.current) setPriceCheck(d.results || []);
    } catch { /* hulpmiddel */ }
  }, []);

  // Prijscheck opnieuw zodra je in de regeltabel zelf prijs, eenheid of omschrijving
  // aanpast. Eerder draaide hij alleen na een AI-ronde of laden, waardoor de melding
  // na handmatig aanpassen bleef staan met het oude percentage (wiki-feedback 26 sep:
  // "ik heb dit aangepast maar hij geeft nog steeds dezelfde foutmelding").
  const draftLinesRef = useRef([]);
  draftLinesRef.current = draft?.lines || [];
  const prijsSleutel = useMemo(() => JSON.stringify((draft?.lines || [])
    .map((l) => [l?.description || '', l?.unit || '', Number(l?.sellingprice) || 0])), [draft?.lines]);
  const eersteprijsSleutelRef = useRef(true);
  useEffect(() => {
    if (eersteprijsSleutelRef.current) { eersteprijsSleutelRef.current = false; return undefined; }
    const regels = draftLinesRef.current;
    // Direct ongeldig maken: een lopende check hoort bij de oude regels en mag ook
    // tijdens de wachttijd niet meer binnenkomen; oude resultaten niet tonen (Codex 26 sep).
    prijsSeqRef.current += 1;
    setPriceCheck([]);
    if (!regels.length) return undefined;
    const t = setTimeout(() => runPriceCheck(regels), 700);
    return () => clearTimeout(t);
  }, [prijsSleutel, runPriceCheck]);

  // Ongedaan-stack: elke assistent-ronde die het concept wijzigt zet de vórige
  // versie hierop; ↩ in de assistent-balk zet hem terug. Puur client-side —
  // het vangnet zolang de assistent geen aparte vraag/wijzig-intentie kent.
  const undoRef = useRef([]);
  const [undoCount, setUndoCount] = useState(0);
  // Versiegeschiedenis (4 sep): elke stand vóór een AI-wijziging, Gripp-laadactie of
  // Gripp-update, met label — wordt bij het gesprek bewaard (draft.versies) en is
  // per versie geheel of gedeeltelijk terug te zetten (VersiesDrawer).
  const [versies, setVersies] = useState([]);
  const [versiesOpen, setVersiesOpen] = useState(false);
  const legVersieVast = useCallback((d, label) => {
    if (!d) return;
    const { versies: _weg, ...kaal } = d; // nooit versies in versies
    setVersies((prev) => [...prev.slice(-39), { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, at: new Date().toISOString(), label,
      subject: kaal.subject || '', description: kaal.description || '', lines: kaal.lines || [], vragen: kaal.vragen || [] }]);
  }, []);
  const pushUndo = useCallback((prevDraft, label = 'Vóór AI-aanpassing') => {
    if (!prevDraft) return;
    undoRef.current = [...undoRef.current.slice(-19), prevDraft];
    setUndoCount(undoRef.current.length);
    legVersieVast(prevDraft, label);
  }, [legVersieVast]);
  const clearUndo = useCallback(() => { undoRef.current = []; setUndoCount(0); }, []);
  // Scope-slot: wat de assistent mag aanpassen. Gaat met elk wijzigingsverzoek mee en
  // wordt server-side afgedwongen — een 'alleen omschrijving' in bericht 1 gold anders
  // niet meer bij bericht 7 (4 sep, offerte 9778).
  const [scopeLock, setScopeLock] = useState('all');
  const scopeRef = useRef('all');
  useEffect(() => { scopeRef.current = scopeLock; }, [scopeLock]);
  const detecteerScope = (tekst) => {
    const t = String(tekst || '').toLowerCase();
    if (/alleen (de |het )?omschrijving|omschrijving alleen|(laat|hou|houd) (de )?(offerte)?regels (ongemoeid|staan|met rust|zoals ze zijn)|regels niet (aanpassen|wijzigen|veranderen)|niet de regels/.test(t)) return 'description';
    if (/alleen (de )?(offerte)?regels|regels alleen|(laat|hou|houd) (de )?omschrijving (ongemoeid|staan|met rust|zoals hij is)|omschrijving niet (aanpassen|wijzigen|veranderen)|niet de omschrijving/.test(t)) return 'lines';
    if (/(alles|beide|ook de regels|ook de omschrijving) (mag|mogen)|scope (los|vrij|uit)|alles aanpassen/.test(t)) return 'all';
    return null;
  };

  // Stuur de hele historie naar de chat-endpoint en verwerk het antwoord.
  const sendToChat = useCallback(async (history) => {
    setChatLoading(true); setError(null);
    const act = startActivity('Offerte-assistent', { path: '/offertes' });
    try {
      const laatsteVraag = [...history].reverse().find((m) => m.role === 'user')?.content || '';
      const refs = await referentiesUitChat(laatsteVraag);
      // Zodra er een DOCUMENT staat (geladen offerte óf gegenereerde omschrijving),
      // is elk chatbericht een WIJZIGINGSVERZOEK op de huidige stand (via
      // /offertes/revise). Vroeger gold dit pas bij bestaande regels, maar dan
      // verwees de assistent "genereer regels 1:1 van offerte X" naar de knop
      // i.p.v. het gewoon te doen (26 aug) — revise kan regels ook aanmaken.
      if (editingOffer || draft) {
        const changeRequest = [...history].reverse().find((m) => m.role === 'user')?.content || '';
        // Recente context mee: "1. ja 2. nee"-antwoorden zijn zonder de gestelde
        // vragen onleesbaar — de AI herinterpreteerde dan de hele offerte (26 aug).
        const chatContext = history.slice(-6).map((m) => ({ role: m.role, content: String(m.content || '').slice(0, 900) }));
        const res = await authFetch(`${BACKEND_URL}/Api/offertes/revise`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lines: draft?.lines || [], description: draft?.description || '',
            subject: draft?.subject || '', changeRequest, clientName: company?.name || '',
            chatContext,
            // Achtergrond (e-mail/notulen/documenten uit de bron-kaart) gaat mee als
            // context — anders kon je bij een bestaande offerte niets bijvoegen (4 sep).
            scope: scopeRef.current,
            projecttype: draft?.projecttype || undefined, // kiest de prijsleidraad voor nieuwe regels
            background: mail ? String(mail).slice(0, 15000) : '',
            backgroundSources: sources.map((s) => `${s.kind || 'bron'}: ${s.title}`),
            // Referentie-offertes ook in de wijzig-flow: "regels 1:1 overnemen
            // van de vorige offerte" kan alleen als de regels meegaan (26 aug).
            references: refs.map(r => ({ number: r.number, subject: r.subject, company: r.company, description: r.description, linesSummary: r.linesSummary })),
          }),
        });
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || 'Aanpassen mislukt');
        pushUndo(draft, `Vóór AI-aanpassing: "${changeRequest.slice(0, 60)}${changeRequest.length > 60 ? '…' : ''}"`);
        const slot = scopeRef.current;
        setDraft((prev) => ({
          ...prev,
          description: slot === 'lines' ? prev.description : (d.description ?? prev.description),
          lines: slot === 'description' ? prev.lines : (Array.isArray(d.lines) ? d.lines : prev.lines),
          vragen: Array.isArray(d.vragen) ? d.vragen : [],
        }));
        if (Array.isArray(d.lines) && d.lines.length) runPriceCheck(d.lines);
        const extra = Array.isArray(d.vragen) && d.vragen.length ? `\n\nNog na te vragen:\n${d.vragen.map((v) => `- ${v}`).join('\n')}` : '';
        setMessages((prev) => [...prev, { role: 'assistant', content: `✏️ ${d.summary || 'De offerte is bijgewerkt — controleer de omschrijving en regels in het document hierboven.'}${extra}` }]);
        act.done('Offerte bijgewerkt');
        return;
      }
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: history,
          clientAgreement: clientAgreement?.agreements || '',
          clientName: clientAgreement?.company_name || company?.name || '',
          references: refs.map(r => ({ number: r.number, subject: r.subject, company: r.company, description: r.description, linesSummary: r.linesSummary })),
          // De actuele stand van het document: de gebruiker kan de omschrijving
          // zelf bewerkt hebben — zonder dit vroeg de assistent om te plakken.
          currentDraft: draft ? { subject: draft.subject || '', description: draft.description || '' } : null,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Chat mislukt');
      // Open vragen horen óók in het gesprek: de gebruiker antwoordt met "1. … 2. …" en
      // dan moet de genummerde lijst terug te lezen zijn (Joris 16 sep). De wijzig-flow
      // deed dit al; de eerste-conceptflow zette ze alleen in het gele vak.
      const vragenBlok = d.ready && d.draft && Array.isArray(d.draft.vragen) && d.draft.vragen.length
        && !/nog na te vragen|open vragen/i.test(d.reply || '')
        ? `\n\nNog na te vragen:\n${d.draft.vragen.map((v, i) => `${i + 1}. ${v}`).join('\n')}` : '';
      setMessages(prev => [...prev, { role: 'assistant', content: `${d.reply || '…'}${vragenBlok}` }]);
      if (d.ready && d.draft) {
        pushUndo(draft, 'Vóór nieuw concept van de assistent');
        // Een handmatig gekozen regeltaal overleeft een nieuw concept in hetzelfde gesprek.
        setDraft((prev) => ({
          ...omzetDefaults(),
          ...(prev?.omzetStart && prev?.omzetEind ? { omzetStart: prev.omzetStart, omzetEind: prev.omzetEind } : {}),
          ...d.draft, language: d.draft.language ?? prev?.language,
        }));
        setPriceCheck([]); setCreateResult(null);
        if (Array.isArray(d.draft.lines) && d.draft.lines.length) runPriceCheck(d.draft.lines);
      }
      act.done(d.ready && d.draft ? 'Concept-offerte staat klaar' : 'Antwoord staat klaar');
    } catch (err) { setError(err.message); act.fail(err.message); } finally { setChatLoading(false); }
  }, [runPriceCheck, clientAgreement, company, references, editingOffer, draft, pushUndo, mail, sources, referentiesUitChat]);

  // ↩ De vorige concept-versie terugzetten (laatste assistent-wijziging ongedaan).
  const undoDraft = () => {
    const vorige = undoRef.current.pop();
    if (!vorige) return;
    setUndoCount(undoRef.current.length);
    legVersieVast(draft, 'Vóór ongedaan maken');
    setDraft(vorige);
    if ((vorige.lines || []).length) runPriceCheck(vorige.lines);
    setMessages((prev) => [...prev, { role: 'assistant', content: '↩ De vorige versie van het concept staat terug.' }]);
  };

  // Herstel vanuit de versiegeschiedenis — geheel, alleen omschrijving of alleen regels.
  const herstelVersie = (v, wat) => {
    if (!v || !draft) return;
    legVersieVast(draft, `Vóór herstel (${wat})`);
    setDraft((prev) => ({
      ...prev,
      ...(wat !== 'regels' ? { description: v.description || '' } : {}),
      ...(wat !== 'omschrijving' ? { lines: Array.isArray(v.lines) ? v.lines : prev.lines } : {}),
      ...(wat === 'alles' && v.subject ? { subject: v.subject } : {}),
    }));
    if (wat !== 'omschrijving' && (v.lines || []).length) runPriceCheck(v.lines);
    setMessages((prev) => [...prev, { role: 'assistant', content: `↩ Versie teruggezet (${v.label || 'versie'} · ${wat}).` }]);
    setVersiesOpen(false);
  };

  const startChat = () => {
    const intro = mail.trim()
      ? `${SOURCE_INTRO_PREFIX}${mail.trim()}`
      : 'Ik wil een offerte opstellen. Stel me de nodige vragen.';
    const history = [{ role: 'user', content: intro }];
    setMessages(history);
    setStarted(true);
    sendToChat(history);
  };

  useEffect(() => {
    if ((draft?.lines || []).length) loadProducts();
  }, [draft?.lines, loadProducts]);

  // Krijgt de tekst uit het chatveld; true = verstuurd (de balk leegt dan het veld).
  const onSend = (invoer) => {
    const text = String(invoer || '').trim();
    if (!text || chatLoading) return false;
    const auto = detecteerScope(text);
    if (auto && auto !== scopeRef.current) {
      scopeRef.current = auto; setScopeLock(auto);
      toast(auto === 'all' ? 'Scope-slot los: alles mag weer aangepast worden' : `🔒 Scope-slot aan: alleen ${auto === 'description' ? 'de omschrijving' : 'de regels'} — blijft staan tot je het wijzigt`, { icon: '🔒' });
    }
    const history = [...messages, { role: 'user', content: text }];
    setMessages(history);
    sendToChat(history);
    return true;
  };

  // Achtergrond → omschrijving: één vaste opdracht via de gewone wijzig-flow (revise),
  // zodat het in de chat terugleesbaar is en met ⌘Z ongedaan gemaakt kan worden.
  const vulOmschrijvingMetAchtergrond = () => {
    if (chatLoading) return;
    const text = 'Vul de omschrijving in op basis van de ACHTERGROND: vervang placeholders zoals [klant], [type film], [doel], [doelgroep], [voorbeeldstijl], [Naam Film] en [datum] door wat de achtergrond zegt, en vul Doel en doelgroep, Stijl, Deliverables, Inzet en Planning aan waar de achtergrond dat onderbouwt. Verzin niets: wat de achtergrond niet vertelt laat je staan en zet je in de vragen. Laat de offerteregels ongemoeid.';
    const history = [...messages, { role: 'user', content: text }];
    setMessages(history); setAchtergrondOpen(false);
    scopeRef.current = 'description'; setScopeLock('description');
    sendToChat(history);
  };

  // Bevindingen van de AI-check als opdracht terug de chat in — de assistent
  // werkt omschrijving (en waar nodig regels) bij en levert een nieuw concept.
  const applyCheckFindings = () => {
    if (!checkResult || chatLoading) return;
    const issues = (checkResult.checks || [])
      .filter((c) => String(c.status || '').toLowerCase() !== 'ok')
      .map((c) => `- ${c.titel}${c.toelichting ? `: ${c.toelichting}` : ''}`);
    const sugg = (checkResult.suggesties || [])
      .map((s) => `- ${s.plek ? `[${s.plek}] ` : ''}${s.van ? `"${s.van}" → ` : ''}${s.naar}`);
    const points = [...issues, ...sugg].join('\n') || checkResult.samenvatting || '';
    const text = `Verwerk deze bevindingen uit de AI-offertecheck in het concept — pas de omschrijving aan en waar nodig ook de offerteregels, en geef het volledige bijgewerkte concept terug:\n\n${points}`;
    const history = [...messages, { role: 'user', content: text }];
    setMessages(history);
    sendToChat(history);
    setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 150);
  };

  // Forceer nu alvast een concept, ook al is niet alles bekend.
  const forceDraft = () => {
    if (chatLoading) return;
    const history = [...messages, { role: 'user', content: 'Maak nu alvast een concept-offerte met wat je nu weet. Zet alle onbekende punten in de open vragen en gebruik "nader te bepalen" waar nodig — verzin geen details.' }];
    setMessages(history);
    sendToChat(history);
  };

  // Sorteer/groepeer regels op sectie in een vaste, logische volgorde.
  const sortLinesBySection = () => setDraft(prev => ({ ...prev, lines: sortBySection(prev.lines || []) }));
  const total = (draft?.lines || []).reduce((s, l) => s + (Number(l.sellingprice) || 0) * (Number(l.amount) || 0), 0);

  // Stap 2 van de wizard: genereer offerteregels uit de (goedgekeurde) omschrijving.
  const [linesLoading, setLinesLoading] = useState(false);
  const genLines = async () => {
    if (!draft?.description || linesLoading) return;
    setLinesLoading(true); setError(null);
    const act = startActivity('Offerteregels genereren', { path: '/offertes' });
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/lines`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: draft.description,
          // Kiest de leidende Gripp-basisofferte (animatie/webinar/draaidag).
          projecttype: draft.projecttype || undefined,
          clientAgreement: clientAgreement?.agreements || '',
          clientName: clientAgreement?.company_name || company?.name || '',
          // Referentie-offertes gaan mee: hun regel-opbouw is leidend (geen
          // standaardregels toevoegen die de klant vorige keer niet had).
          // Omschrijving mee: daarin staan de deliverables met lengtes, nodig om uren per film te vergelijken.
          references: references.map(r => ({ number: r.number, subject: r.subject, company: r.company, description: r.description, linesSummary: r.linesSummary })),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Regels genereren mislukt');
      const lines = d.lines || [];
      setDraft(prev => ({ ...prev, lines }));
      setPriceCheck([]);
      if (lines.length) runPriceCheck(lines);
      act.done(`${lines.length} regels staan klaar`);
    } catch (err) { setError(err.message); act.fail(err.message); } finally { setLinesLoading(false); }
  };

  // Basisoffertes voor het "Start uit basisofferte"-menu (lui geladen).
  const loadBasisOffers = useCallback(async () => {
    if (basisOffers) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/offertes/basisoffertes`);
      const d = await leesJson(r, 'Basisoffertes laden');
      setBasisOffers(d.basisoffertes || []);
      setBasisCatalogus(d.catalogus || []);
    } catch (e) { meldFout(e, 'Basisoffertes laden'); setBasisOffers([]); setBasisCatalogus([]); }
  }, [basisOffers]);
  // Regels in één keer vervangen (slepen, secties, product kiezen). Met een label
  // krijgt de wijziging een ongedaan-toast (+ Ctrl+Z) — vangnet voor herordenen/verwijderen.
  const regelsRef = useRef([]);
  regelsRef.current = draft?.lines || [];
  // Snel achter elkaar verplaatsen (↑↓↓) wordt één ongedaan-stap met één toast.
  // Ongedaan maken herstelt alleen als de regels sindsdien niet zijn veranderd (geen
  // prijswijziging overschrijven, geen regels in een ander concept zetten).
  const laatsteOngedaanRef = useRef(null);
  const setLines = useCallback((nieuw, label) => {
    let vorige = regelsRef.current;
    regelsRef.current = nieuw;
    setDraft((prev) => (prev ? { ...prev, lines: nieuw } : prev));
    if (!label) return;
    const l = laatsteOngedaanRef.current;
    if (l && l.label === label && l.na === vorige && Date.now() - l.tijd < 8000) {
      vorige = l.vorige;
      toast.dismiss(l.id);
    }
    const herstel = async () => {
      if (regelsRef.current !== nieuw) {
        throw new Error('de regels zijn daarna nog gewijzigd');
      }
      regelsRef.current = vorige;
      setDraft((prev) => (prev ? { ...prev, lines: vorige } : prev));
    };
    const id = biedOngedaanAan(label, herstel, { eigenKnop: true });
    laatsteOngedaanRef.current = { id, label, vorige, na: nieuw, tijd: Date.now() };
  }, []);
  // Regels van een Gripp-basisofferte als startpunt. AI Specialist/AI Credits
  // vallen weg als de omschrijving niets over AI zegt.
  const startFromBasis = async (id) => {
    setLinesLoading(true); setError(null);
    try {
      const q = new URLSearchParams({ id: String(id), tekst: draft?.description || '' });
      const r = await authFetch(`${BACKEND_URL}/Api/offertes/basisoffertes/sjabloon?${q}`);
      const d = await leesJson(r, 'Basisofferte laden');
      const lines = d.lines || [];
      setDraft((prev) => ({ ...(prev || {}), lines }));
      setPriceCheck([]);
      if (lines.length) runPriceCheck(lines);
      toast.success(`${lines.length} regels uit “${d.basis?.naam || 'basisofferte'}”`);
    } catch (e) { meldFout(e, 'Basisofferte laden'); } finally { setLinesLoading(false); }
  };

  // Gestuurde flow: de rechter-kaarten focussen automatisch op de huidige stap
  // (omschrijving → regels → details). Alleen bij een stap-OVERGANG, zodat handmatig
  // in-/uitklappen niet wordt overruled.
  const lastStageRef = useRef('');
  useEffect(() => {
    const stage = !draft ? '' : (createResult?.ok ? 'gripp' : ((draft.lines || []).length ? 'regels' : 'oms'));
    if (!stage || stage === lastStageRef.current) return;
    lastStageRef.current = stage;
  }, [draft, createResult]);

  // Holistische AI-check op de concept-offerte (logica, bron, prijsafspraken, BTW, spelling).
  const runCheck = async () => {
    if (!draft || checkLoading) return;
    setCheckLoading(true); setError(null); setCheckResult(null);
    const act = startActivity('Offerte-check', { path: '/offertes' });
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft,
          company: company ? { id: company.id, name: company.name } : null,
          source: mail || '',
          clientAgreement: clientAgreement?.agreements || '',
          references: references.map(r => ({ number: r.number, subject: r.subject, company: r.company })),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Check mislukt');
      setCheckResult(d);
      act.done('Check-resultaat staat klaar');
    } catch (err) { setError(err.message); act.fail(err.message); } finally { setCheckLoading(false); }
  };

  // Losse check op een BESTAANDE Gripp-offerte (mode 'check'): backend laadt de
  // offerte + regels op nummer en draait dezelfde controle.
  const [checkOffer, setCheckOffer] = useState(null);
  // Vervolg-chat op de check (2-weg gesprek over de gecontroleerde offerte).
  const [checkMessages, setCheckMessages] = useState([]);
  const [checkChatInput, setCheckChatInput] = useState('');
  const [checkChatLoading, setCheckChatLoading] = useState(false);
  const runCheckExisting = async () => {
    const number = checkOffer?.number;
    if (!number || checkLoading) return;
    setCheckLoading(true); setError(null); setCheckResult(null); setCheckMessages([]);
    const act = startActivity(`Offerte ${number} controleren`, { path: '/offertes' });
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offerNumber: number }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Check mislukt');
      setCheckResult(d);
      act.done('Check-resultaat staat klaar');
    } catch (err) { setError(err.message); act.fail(err.message); } finally { setCheckLoading(false); }
  };
  // Stel een vervolgvraag over de gecontroleerde offerte (hergebruikt /offertes/chat
  // met z'n zoek-/lees-tools zodat de AI oude/soortgelijke offertes kan opzoeken).
  const sendCheckChat = async (preset) => {
    const text = (preset || checkChatInput).trim();
    if (!text || checkChatLoading || !checkOffer) return;
    const contextMsg = { role: 'user', content:
      `Context: ik laat offerte ${checkOffer.number} (${checkOffer.subject}${checkOffer.company ? `, klant ${checkOffer.company}` : ''}) controleren.`
      + (checkResult?.samenvatting ? ` Samenvatting van de check: ${checkResult.samenvatting}` : '')
      + ` Beantwoord mijn vervolgvragen hierover. Je mag oude/soortgelijke offertes opzoeken (zoek_offertes) en lezen (lees_offerte). Verzin niets; zeg het als iets niet te vinden is.` };
    const userMsg = { role: 'user', content: text };
    const history = [...checkMessages, userMsg];
    setCheckMessages(history); setCheckChatInput(''); setCheckChatLoading(true); setError(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [contextMsg, ...history], clientName: checkOffer.company || '' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Chat mislukt');
      setCheckMessages(prev => [...prev, { role: 'assistant', content: d.reply || '…' }]);
    } catch (err) { setError(err.message); } finally { setCheckChatLoading(false); }
  };

  // Fase 1: NL↔EN product-audit (read-only koppel-voorstel + stale-flags).
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  // Curatie (Fase 2): per paar goedkeuren + juist tarief; daarna dry-run + doorvoeren.
  const [approved, setApproved] = useState({});       // en_id → bool
  const [targetPrices, setTargetPrices] = useState({}); // en_id → string
  const [cleanupStep, setCleanupStep] = useState('review'); // 'review' | 'preview'
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [applyResults, setApplyResults] = useState(null);
  const openAudit = async (refresh = false) => {
    setAuditOpen(true);
    if (audit && !refresh) return;
    setAuditLoading(true); setAuditError(null); setCleanupStep('review'); setCleanupPreview(null); setApplyResults(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/products-audit${refresh ? '?refresh=1' : ''}`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `Audit mislukt (${res.status})`);
      setAudit(d);
      // Standaard: alle paren goedgekeurd, doeltarief = NL-tarief (per rij aanpasbaar).
      const ap = {}, tp = {};
      (d.pairs || []).forEach(p => { ap[p.en.id] = true; tp[p.en.id] = p.nl.price != null ? String(p.nl.price) : ''; });
      setApproved(ap); setTargetPrices(tp);
    } catch (err) { setAuditError(err.message); } finally { setAuditLoading(false); }
  };
  // Dry-run: vraag het opschoon-voorstel op voor de goedgekeurde paren.
  const runCleanupPreview = async () => {
    const pairs = (audit?.pairs || [])
      .filter(p => approved[p.en.id])
      .map(p => ({ nl_id: p.nl.id, en_id: p.en.id, targetPrice: targetPrices[p.en.id] !== '' ? Number(targetPrices[p.en.id]) : null }));
    if (!pairs.length) { setError('Geen goedgekeurde paren geselecteerd.'); return; }
    setCleanupBusy(true); setError(null); setApplyResults(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/products-cleanup-preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pairs }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Voorstel maken mislukt');
      setCleanupPreview(d.changes || []); setCleanupStep('preview');
    } catch (err) { setError(err.message); } finally { setCleanupBusy(false); }
  };
  // Doorvoeren in Gripp (na expliciete bevestiging).
  const applyCleanup = async () => {
    const changes = (cleanupPreview || [])
      .filter(c => c.price?.changed || c.description?.changed)
      .map(c => ({ en_id: c.en_id, price: c.price?.changed ? c.price.to : undefined, description: c.description?.changed ? c.description.to : undefined }));
    if (!changes.length) { setError('Niets te wijzigen.'); return; }
    if (!(await bevestig({ titel: `${changes.length} Engels(e) product(en) bijwerken in Gripp?`, tekst: 'Tarief en/of omschrijving worden aangepast in de Gripp-catalogus.', knop: 'Bijwerken in Gripp', gevaarlijk: true }))) return;
    setCleanupBusy(true); setError(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/products-cleanup-apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changes }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Doorvoeren mislukt');
      setApplyResults(d);
    } catch (err) { setError(err.message); } finally { setCleanupBusy(false); }
  };

  // Meest recente offertes (snel selecteren/openen) — geladen bij openen van de check-modus.
  const [recentOffers, setRecentOffers] = useState([]);
  useEffect(() => {
    if (mode !== 'check' || recentOffers.length) return;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/offertes/recent-offers?limit=12`);
        const d = await res.json();
        if (res.ok) setRecentOffers(d.offers || []);
      } catch { /* stil */ }
    })();
  }, [mode, recentOffers.length]);

  // Wat er voor het laatst naar Gripp is geschreven — bepaalt de 'nog niet
  // opgeslagen wijzigingen'-melding en of de knop create of update doet.
  const pushedRef = useRef(null);
  // Taal van de regels: handmatige keuze leeft in het concept zelf (zo blijft 'ie bewaard
  // in het gesprek), anders herkennen we 'm aan de brontekst.
  const language = draft?.language ?? detectDraftLanguage(draft);
  const snapshotOf = (d) => JSON.stringify({
    s: d?.subject, d: d?.description, l: d?.lines, g: d?.language ?? detectDraftLanguage(d),
    // Vrije velden horen bij wat er in Gripp staat — een gewijzigd Doel zonder
    // "werk bij" is óók een niet-opgeslagen wijziging.
    f: [d?.doel, d?.dienst, d?.omvang, d?.projecttype],
  });
  const draftSnapshot = () => snapshotOf(draft);
  const hasUnsavedGrippChanges = createResult?.ok && pushedRef.current && pushedRef.current !== draftSnapshot();
  // Bestaande offerte in Gripp geladen/aangemaakt → bijwerken kan zonder opnieuw een
  // klant te kiezen (die zit al aan de offerte). Alleen een NIEUWE offerte heeft company nodig.
  const isExistingOffer = !!(createResult?.ok && createResult.offerId);

  // Wat Gripp nodig heeft voordat hij een offerte accepteert. Eén lijst, zodat
  // de gebruiker in één keer ziet wat er mist in plaats van veld voor veld —
  // en zonder klant gebeurde er eerst helemaal niets (Joris, 11 sep 2026).
  const ontbrekendeGegevens = () => {
    const mist = [];
    if (!isExistingOffer && !company?.id) mist.push('een klant (kies die bovenaan bij Eigenschappen)');
    if (!String(draft?.subject || '').trim()) mist.push('een onderwerp');
    if (!(draft?.lines || []).length) mist.push('offerteregels (genereer ze eerst uit de omschrijving)');
    if (!draft?.omzetStart || !draft?.omzetEind) mist.push('de omzetverdeling voor de Gripp-rapportage');
    if (!draft?.projecttype) mist.push('projecttype');
    if (!draft?.doel) mist.push('doel');
    if (!draft?.dienst) mist.push('dienst');
    if (!draft?.omvang) mist.push('omvang');
    return mist;
  };

  const grippMelding = (mist) => {
    const opsomming = mist.length === 1
      ? mist[0]
      : `${mist.slice(0, -1).join(', ')} en ${mist[mist.length - 1]}`;
    return `Nog niet compleet voor Gripp: er ontbreekt ${opsomming}. `
      + 'Gripp weigert een offerte zonder deze velden, en weigert daarna ook elke wijziging.';
  };

  // De "nog niet compleet"-melding leeft mee met de velden: zodra de klant of een ander veld is ingevuld,
  // wordt de lijst korter en verdwijnt de melding vanzelf (bleef eerst staan tot je hem wegklikte — Joris, 22 sep 2026).
  useEffect(() => {
    if (!error || !String(error).startsWith('Nog niet compleet voor Gripp')) return;
    const mist = ontbrekendeGegevens();
    const nieuw = mist.length ? grippMelding(mist) : null;
    if (nieuw !== error) setError(nieuw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isExistingOffer, company?.id, draft?.subject, draft?.lines?.length, draft?.omzetStart, draft?.omzetEind,
    draft?.projecttype, draft?.doel, draft?.dienst, draft?.omvang]);

  const createInGripp = async () => {
    if (!draft) return;
    const mist = ontbrekendeGegevens();
    if (mist.length) {
      setError(grippMelding(mist));
      openEigenschappen();
      return;
    }
    setCreating(true);
    const isUpdate = createResult?.ok && createResult.offerId;
    const act = startActivity(isUpdate ? 'Offerte bijwerken in Gripp' : 'Offerte aanmaken in Gripp', { path: '/offertes' });
    // Al aangemaakt? → BIJWERKEN in plaats van een tweede offerte aanmaken.
    if (isUpdate) {
      legVersieVast(draft, 'Vóór bijwerken in Gripp');
      try {
        // Btw-controle: een regel op 0% gaat pas na een bewuste keuze naar Gripp.
        const { res, d, geannuleerd } = await postMetBtwControle(`${BACKEND_URL}/Api/offertes/update-offer`, {
          offerId: createResult.offerId, subject: draft.subject, description: draft.description,
          // Volgorde en groepen precies zoals getoond (elke sectie één aaneengesloten blok).
          lines: normaliseerRegels(draft.lines), language,
          doel: draft.doel || undefined, dienst: draft.dienst || undefined,
          omvang: draft.omvang || undefined, projecttype: draft.projecttype || undefined,
          omzetStart: draft.omzetStart || undefined, omzetEind: draft.omzetEind || undefined,
        });
        if (geannuleerd) { act.fail('Gestopt bij de btw-controle'); return; }
        if (!res.ok) throw new Error(d.error || 'Bijwerken mislukt');
        pushedRef.current = draftSnapshot();
        setCreateResult((prev) => ({ ...prev, updatedAt: Date.now(), updateError: null, englishGaps: d.englishGaps || [] }));
        act.done('Offerte is bijgewerkt in Gripp');
        // Meteen door naar de bijgewerkte offerte — dat is vrijwel altijd de
        // volgende handeling. Blokkeert de browser de popup, dan staat de
        // "Open in Gripp"-knop er sowieso.
        const grippUrl = buildGrippOfferUrl(createResult.offerId, company?.id);
        if (grippUrl) window.open(grippUrl, '_blank', 'noopener');
      } catch (err) { setCreateResult((prev) => ({ ...prev, updateError: err.message })); act.fail(err.message); }
      finally { setCreating(false); }
      return;
    }
    setCreateResult(null);
    try {
      // Btw-controle: een regel op 0% gaat pas na een bewuste keuze naar Gripp.
      const { res, d, geannuleerd } = await postMetBtwControle(`${BACKEND_URL}/Api/offertes/create-gripp`, {
        subject: draft.subject, description: draft.description,
        // Volgorde en groepen precies zoals getoond (elke sectie één aaneengesloten blok).
        lines: normaliseerRegels(draft.lines),
        companyId: company.id, contactId: contact?.id || undefined, accountmanagerId: accountManager?.id || undefined,
        // Verplichte Gripp-velden — anders weigert de Gripp-UI elke latere save
        doel: draft.doel || undefined, dienst: draft.dienst || undefined,
        omvang: draft.omvang || undefined, projecttype: draft.projecttype || undefined,
        omzetStart: draft.omzetStart || undefined, omzetEind: draft.omzetEind || undefined,
        language,
      });
      if (geannuleerd) { act.fail('Gestopt bij de btw-controle'); return; }
      // De echte reden van Gripp naar voren halen (wiki: 'je krijgt geen duidelijke foutmelding').
      if (!res.ok) throw new Error(d.error || d.detail || d.message || (d.gripp && JSON.stringify(d.gripp).slice(0, 300)) || `Aanmaken mislukt (HTTP ${res.status})`);
      // Zonder id is er niets aangemaakt (22 okt: Gripp plat, toch 'gelukt').
      if (!d.offerId) throw new Error('Gripp gaf geen offerte-id terug; de offerte is waarschijnlijk niet aangemaakt. Controleer in Gripp en probeer het opnieuw.');
      setCreateResult({ ok: true, offerId: d.offerId, offerNumber: d.offerNumber, pdfUrl: d.pdfUrl, viewUrl: d.viewUrl, englishGaps: d.englishGaps || [] });
      pushedRef.current = draftSnapshot();
      // Leg vast in het gesprek dat deze offerte in Gripp staat — anders 'weet' de
      // assistent dat later niet en zegt 'ie ten onrechte dat er niks is aangemaakt.
      setMessages((prev) => [...prev, { role: 'assistant', content:
        `✅ Offerte aangemaakt in Gripp: "${draft.subject || 'concept'}"${d.offerNumber ? ` — offertenummer ${d.offerNumber}` : ''}${company?.name ? ` voor ${company.name}` : ''} (staat als concept in Gripp).` }]);
      act.done(`Offerte ${d.offerNumber || ''} staat in Gripp`.trim());
      // Meteen persisteren met de verse waarden — niet wachten op de debounce.
      setTimeout(() => saveChatRef.current?.({ silent: true, cr: { ok: true, offerId: d.offerId, offerNumber: d.offerNumber } }), 0);
      // Vanuit de projectflow gestart? Dan het gesprek samenvatten als
      // projectnotitie — het 'waarom' van de offerte hoort bij het project.
      if (flowProjectId) {
        authFetch(`${BACKEND_URL}/Api/offertes/gesprek-notitie`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            projectId: flowProjectId, offerNumber: d.offerNumber || '',
            clientName: company?.name || '', messages,
          }),
        }).then(async (r) => {
          if (r.ok) toast.success('Gesprek samengevat bij de projectnotities');
          else toast(`Gesprek-notitie niet bewaard: ${(await r.json().catch(() => ({}))).error || `HTTP ${r.status}`}`, { icon: '⚠️' });
        }).catch(() => toast('Gesprek-notitie niet bewaard (netwerkfout)', { icon: '⚠️' }));
      }
    } catch (err) { setCreateResult({ error: err.message }); act.fail(err.message); } finally { setCreating(false); }
  };

  // silent = automatische achtergrond-opslag (geen spinner/foutmelding).
  const saveChatInFlight = useRef(false);
  const saveChat = async ({ silent = false, cr, forceConcept = false } = {}) => {
    if (!messages.length) return;
    // Autosave-races (Codex r10): niet stil saven tijdens het Gripp-aanmaken
    // (een concept-save kon ná de in_gripp-save landen), en zonder chat-id geen
    // twee gelijktijdige creates van het chatrecord.
    if (silent && creating) return;
    if (silent && !currentChatId && saveChatInFlight.current) return;
    saveChatInFlight.current = true;
    if (!silent) { setSaving(true); setError(null); }
    // cr = verse createResult direct na het aanmaken — state-updates zijn async,
    // dus zonder override kon de save nog met de oude (lege) status lopen en
    // bleef een heropend gesprek op 'Concept' staan terwijl de offerte al in
    // Gripp stond (KBP/PON Bike, 20 aug).
    const effCr = cr ?? createResult;
    try {
      // Heldere titel: liefst het concept-onderwerp; anders klant + gebruikte zoektermen
      // (bv. "Fontys · Portvolio"); pas als laatste val terug op de bron-tekst.
      const termTitle = [company?.name, ...sourceTerms].filter(Boolean).join(' · ');
      const title = draft?.subject
        || (termTitle || (messages.find(m => m.role === 'user')?.content || '').replace(/\s+/g, ' ').slice(0, 60))
        || 'Offerte';
      const body = {
        id: currentChatId || undefined, user_email: userEmail, title, messages,
        draft: draft ? {
          ...draft,
          ...(effCr?.offerNumber ? { grippOfferNumber: effCr.offerNumber } : {}),
          // wachtrij van gesplitste offertes overleeft herladen/heropenen
          offerteWachtrij: offerteWachtrij.length ? offerteWachtrij : undefined,
          // referentie-offertes horen bij het gesprek: zonder dit waren ze na
          // een refresh weg en faalde '1:1 dupliceren' stilletjes (26 aug)
          offerteReferences: references.length ? references : undefined,
          versies: versies.length ? versies : undefined,
          scopeLock: scopeLock !== 'all' ? scopeLock : undefined,
        } : draft,
        company, sources,
        status: forceConcept ? 'concept' : (effCr?.ok ? 'in_gripp' : 'concept'),
        gripp_offer_id: forceConcept ? null : (effCr?.offerId || undefined),
        // De engine weigert stil terugzetten van 'in_gripp' (autosave-race);
        // bij 'volgende offerte uit dit gesprek' is de downgrade juist bedoeld.
        force_status: forceConcept || undefined,
      };
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/offerte-chats`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Opslaan mislukt');
      if (d.id) setCurrentChatId(d.id);
      loadSavedList();
    } catch (err) { if (!silent) setError(err.message); }
    finally { saveChatInFlight.current = false; if (!silent) setSaving(false); }
  };

  // Auto-opslaan: elk gesprek wordt automatisch bewaard (debounced) zodat je er
  // altijd naar terug kunt via "Opgeslagen offertes openen" — ook zonder handmatig opslaan.
  const saveChatRef = useRef();
  saveChatRef.current = saveChat;
  useEffect(() => {
    if (!messages.length) return undefined;
    const t = setTimeout(() => { saveChatRef.current?.({ silent: true }); }, 1500);
    return () => clearTimeout(t);
  }, [messages, draft, company, createResult, sources, versies, scopeLock]);

  const openChat = async (item) => {
    if (!item) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/offerte-chats/${item.id}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Laden mislukt');
      setCurrentChatId(d.id);
      setMessages(d.messages || []);
      // Bron terugwinnen uit het eerste bericht — anders draait de offerte-check na
      // heropenen met source:'' en vergelijkt 'ie niets meer met de oorspronkelijke mail.
      const intro = (d.messages || []).find((m) => m.role === 'user')?.content || '';
      setMail(intro.startsWith(SOURCE_INTRO_PREFIX) ? intro.slice(SOURCE_INTRO_PREFIX.length) : '');
      const { versies: bewaardeVersies, scopeLock: bewaardSlot, ...kaalDraft } = d.draft || {};
      setDraft(d.draft ? kaalDraft : null);
      setVersies(Array.isArray(bewaardeVersies) ? bewaardeVersies : []);
      setScopeLock(bewaardSlot || 'all'); scopeRef.current = bewaardSlot || 'all';
      setCompany(d.company || null);
      setSources(d.sources || []);
      fetchAgreement(d.company || null);
      loadContacts(d.company || null);
      setCreateResult(d.gripp_offer_id
        ? { ok: true, offerId: d.gripp_offer_id, offerNumber: d.draft?.grippOfferNumber || null }
        : null);
      setOfferteWachtrij(Array.isArray(d.draft?.offerteWachtrij) ? d.draft.offerteWachtrij : []);
      const refs = Array.isArray(d.draft?.offerteReferences) ? d.draft.offerteReferences : [];
      setReferences(refs);
      if (refs.length) versReferenties(refs);
      setStarted(true); setPriceCheck([]); setChatsOverride(null);
      setCompanyQuery(''); setCompanyOpts([]); // zoekterm van het vórige gesprek niet meeslepen
      if (d.draft?.lines?.length) runPriceCheck(d.draft.lines);
    } catch (err) { setError(err.message); }
  };

  // Gesprek verwijderen (🗑 in de lijst) — verdwijnt ook uit 'Eerdere gesprekken'
  // van collega's; de Gripp-offerte zelf blijft uiteraard gewoon staan.
  const verwijderChat = async (c) => {
    const label = c.title || 'dit gesprek';
    const extra = c.status === 'in_gripp' ? 'De offerte in Gripp blijft bestaan; alleen het gesprek verdwijnt.' : 'Het gesprek verdwijnt ook uit "Eerdere gesprekken" van collega\'s.';
    if (!(await bevestig({ titel: `"${label}" verwijderen?`, tekst: extra, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/offerte-chats/${c.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      if (currentChatId === c.id) newChat();
      loadSavedList();
    } catch (e) { setError(e.message); }
  };

  const newChat = () => {
    setOfferteWachtrij([]);
    setCurrentChatId(null); setMessages([]); setDraft(null); setCompany(null);
    setStarted(false); setMail(''); setCreateResult(null); setPriceCheck([]);
    setVersies([]); setScopeLock('all'); scopeRef.current = 'all';
    setClientAgreement(null); setContacts([]); setContact(null); setReferences([]); setRefOpts([]);
    setSourceTerms([]); setSrcResults(null); setSrcQuery(''); setSources([]);
    setEditingOffer(null); setChatsOverride(null); setCompanyQuery(''); setCompanyOpts([]);
    // Zonder deze reset blijft de snapshot van de vórige offerte staan en meldt het
    // scherm bij het volgende concept meteen 'nog niet opgeslagen wijzigingen'.
    pushedRef.current = null;
    clearUndo();
  };

  // Bestaande Gripp-offerte in de split-view laden en de chat scherpstellen om 'm aan
  // te passen (omschrijving + regels via /offertes/revise). Vervangt de losse
  // 'Omschrijving bijwerken' / 'Offerte aanpassen'-tabs.
  const loadExistingOffer = async (number) => {
    const n = String(number || '').trim().replace(/\D/g, '');
    if (!n) return;
    setLoadDlg({ q: number, loading: true });
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/offer?number=${encodeURIComponent(n)}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Offerte niet gevonden');
      const o = d.offer || {};
      newChat();
      setMode('new');
      const loaded = {
        subject: o.subject || `Offerte ${n}`, description: o.description || '', lines: d.lines || [], vragen: [],
        // Vrije velden uit Gripp — anders toont het eigenschappen-paneel "⚠ 4"
        // terwijl ze in Gripp allang gevuld zijn.
        doel: o.doel || undefined, dienst: o.dienst || undefined,
        omvang: o.omvang || undefined, projecttype: o.projecttype || undefined,
        // Omzetverdeling zoals die nú in Gripp staat — leeg blijft leeg, zodat
        // het paneel er terecht voor waarschuwt.
        omzetStart: o.omzetStart || undefined, omzetEind: o.omzetEind || undefined,
      };
      setDraft(loaded);
      setVersies([]); setScopeLock('all'); scopeRef.current = 'all';
      legVersieVast(loaded, `Geladen uit Gripp (offerte ${o.number || n})`);
      // Wat er nu op het scherm staat kómt uit Gripp — dus nog géén niet-opgeslagen
      // wijzigingen. Zonder deze regel blijft pushedRef leeg en meldt het scherm nooit
      // dat je iets hebt aangepast dat nog niet is teruggeschreven.
      pushedRef.current = snapshotOf(loaded);
      if (o.companyId || o.company) setCompany({ id: o.companyId || o.company?.id, name: o.company?.companyname || o.company?.searchname || o.company?.name || '' });
      setCreateResult({ ok: true, offerId: o.id, offerNumber: o.number, pdfUrl: o.pdfUrl, viewUrl: o.viewUrl });
      setEditingOffer({ number: o.number || n, id: o.id });
      setStarted(true);
      setMessages([{ role: 'assistant', content:
        `Ik heb offerte ${o.number || n}${o.subject ? ` (${o.subject})` : ''} geladen — de omschrijving en regels staan in het document hierboven, de Gripp-velden in het eigenschappen-paneel. Wat wil je aanpassen? Bijvoorbeeld: "zet alle deliverables van seconden naar minuten", of "haal draaidag 2 eruit". Daarna werk je 'm bij in Gripp.` }]);
      if ((d.lines || []).length) runPriceCheck(d.lines);
      setLoadDlg(null);
    } catch (e) { setLoadDlg({ q: number, loading: false, error: e.message }); }
  };

  // Diep-link: ?number=9643 (bv. vanuit Frits' 'Offerte aanpassen') → meteen laden.
  // Frits zet de wijzigingsmail klaar in sessionStorage; die zetten we voor in het
  // chatveld zodat je 'm alleen nog hoeft te versturen. (Werd nergens meer uitgelezen
  // sinds de losse 'Offerte aanpassen'-tab uit de pagina verdween.)
  const didAutoloadRef = useRef(false);
  useEffect(() => {
    if (didAutoloadRef.current || !initialOfferNumber) return;
    didAutoloadRef.current = true;
    let seed = '';
    try {
      seed = sessionStorage.getItem('frits-offerte-change') || '';
      if (seed) sessionStorage.removeItem('frits-offerte-change');
    } catch { /* prefill is optioneel */ }
    loadExistingOffer(initialOfferNumber).then(() => { if (seed) zetChatInvoer(seed); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOfferNumber]);

  // Doorgaan naar de vólgende offerte binnen HETZELFDE gesprek: gesprek, klant, bronnen
  // en referenties blijven staan; alleen het concept/regels/checks worden geleegd zodat
  // je meteen de volgende deliverable kunt uitwerken zonder opnieuw context te geven.
  // Wachtrij voor gesplitste offertes: deel 1 staat in het document, de rest
  // wacht tot deel 1 in Gripp staat (of de gebruiker eerder doorschakelt).
  const [offerteWachtrij, setOfferteWachtrij] = useState([]);
  const gesplitsteOffertes = useMemo(() => splitsOffertes(draft?.description), [draft?.description]);

  const splitsNu = () => {
    const delen = splitsOffertes(draft?.description);
    if (!delen) return;
    pushUndo(draft);
    setOfferteWachtrij(delen.slice(1));
    // Deel 1 heeft vaak geen eigen 'Offerte 1'-kop — dan is de huidige titel beter.
    setDraft((prev) => ({ ...prev, subject: delen[0].hadKop ? delen[0].subject : (prev?.subject || delen[0].subject), description: delen[0].description, lines: [], vragen: prev?.vragen || [] }));
    setPriceCheck([]);
    setMessages((prev) => [...prev, { role: 'assistant', content:
      `✂️ Gesplitst in ${delen.length} aparte offertes. Offerte 1 ("${delen[0].subject}") staat nu in het document — genereer de regels en zet hem in Gripp. Daarna zet je met één klik de volgende klaar.` }]);
  };

  const volgendeUitWachtrij = () => {
    const [kop, ...rest] = offerteWachtrij;
    if (!kop) return;
    clearUndo();
    setOfferteWachtrij(rest);
    setPriceCheck([]); setCheckResult(null); setCreateResult(null); zetChatInvoer('');
    setTimeout(() => saveChatRef.current?.({ silent: true, forceConcept: true }), 0);
    setDraft((prev) => ({ ...(prev || {}), subject: kop.subject, description: kop.description, lines: [], vragen: [] }));
    setMessages((prev) => [...prev, { role: 'assistant', content:
      `➡️ Offerte "${kop.subject}" staat nu in het document${rest.length ? ` (nog ${rest.length} in de wachtrij)` : ' — dit was de laatste'}. Genereer de regels en zet hem in Gripp.` }]);
  };

  const nextOfferFromChat = () => {
    clearUndo();
    setOfferteWachtrij([]);
    setDraft(null); setPriceCheck([]); setCheckResult(null); setCreateResult(null); zetChatInvoer('');
    // Expliciet terug naar concept: de vorige offerte staat in Gripp, dit
    // gesprek gaat verder met een nieuwe.
    setTimeout(() => saveChatRef.current?.({ silent: true, forceConcept: true }), 0);
    setMessages((prev) => [...prev, { role: 'assistant', content: 'Top — die offerte staat in Gripp. Welke deliverable uit dit gesprek wil je nu als offerte uitwerken? Geef de naam en eventuele bijzonderheden (lengte, formaat, ondertiteling).' }]);
    setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 120);
  };

  // Gesprekken-paneel: op desktop de vaste linkerkolom (vervangt hier de secundaire
  // nav — zie hidesSecondaryNav in config/navigation.js), op mobiel een blok onder de
  // wizard. Vanuit elke modus klikbaar → springt naar 'Nieuwe offerte'.
  // Automatisch: open zolang je nog aan het opstarten bent, dicht zodra het gesprek loopt
  // (split-view heeft dan alle breedte nodig). Handmatig te overrulen via de strip/knop.
  // ── Documentweergave: state en afleidingen ─────────────────────────
  // Breed genoeg voor een persistent eigenschappen-paneel? Daaronder een Drawer.
  const widePropsPanel = useMediaQuery('(min-width:1400px)');
  const [threadOpen, setThreadOpen] = useState(false);
  const [propsDrawerOpen, setPropsDrawerOpen] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const companyInputRef = useRef(null);

  const grippFieldsMissing = missingGrippFields(draft, GRIPP_FIELD_OPTIONS);
  const klantMissing = !isExistingOffer && !company?.id;
  const omzetMissing = !!draft && (!draft.omzetStart || !draft.omzetEind);
  const eigenschappenMissing = grippFieldsMissing.length + (klantMissing ? 1 : 0) + (omzetMissing ? 1 : 0);
  const priceCheckWarnings = priceCheck.filter((pp) => pp?.level === 'warn' || pp?.level === 'check').length;

  // Eén bron voor "wat nu?" — zie offerteFlow.js (getest in offerteFlow.test.js).
  const flowAction = nextAction({
    started, draft, company, isExistingOffer, createResult, editingOffer,
    hasUnsavedGrippChanges, clientAgreement, priceCheckWarnings,
    englishGaps: language === 'en' ? (createResult?.englishGaps || []).length : 0,
    missingGrippFields: grippFieldsMissing,
    busy: { chat: chatLoading, lines: linesLoading, creating, checking: checkLoading },
  });

  const openEigenschappen = () => {
    if (!widePropsPanel) setPropsDrawerOpen(true);
    // Drawer moet eerst mounten voordat het veld focus kan krijgen.
    setTimeout(() => companyInputRef.current?.focus(), 250);
  };

  const handleFlowAction = (key) => {
    if (key === 'genLines') return genLines();
    // Vóór "Naar Gripp" eerst het pre-flight-vel: één overzicht van klant,
    // regels, verplichte velden, prijscheck en AI-check — in plaats van losse
    // waarschuwingen. Doorzetten gebeurt vanuit het vel zelf.
    if (key === 'gripp') return setPreflightOpen(true);
    if (key === 'check') return runCheck();
    if (key === 'kiesKlant') return openEigenschappen();
    if (key === 'volgende') return nextOfferFromChat();
    if (key === 'forceDraft') return forceDraft();
    if (key === 'openGripp') {
      const url = buildGrippOfferUrl(createResult?.offerId, company?.id);
      if (url) window.open(url, '_blank', 'noopener');
    }
    return undefined;
  };

  // Elke badge klikt door naar de plek waar je het oplost.
  const handleBadgeClick = (key) => {
    if (key === 'vragen' || key === 'notities') setThreadOpen(true);
    else if (key === 'verplicht' || key === 'engels' || key === 'afspraken') openEigenschappen();
    else if (key === 'nietOpgeslagen') setPreflightOpen(true);
    // prijscheck: de Historie-kolom staat al in de regeltabel zelf
  };

  // Verplichte velden nooit verstopt (FF-REV-004): ontbreekt er iets zodra er
  // regels staan, dan opent de drawer één keer vanzelf; de teller blijft staan.
  const autoOpenedPropsRef = useRef(false);
  useEffect(() => {
    if (widePropsPanel || !draft) { autoOpenedPropsRef.current = false; return; }
    if ((draft.lines || []).length > 0 && eigenschappenMissing > 0 && !autoOpenedPropsRef.current) {
      autoOpenedPropsRef.current = true;
      setPropsDrawerOpen(true);
    }
  }, [widePropsPanel, draft, eigenschappenMissing]);

  const eigenschappenProps = {
    company, companyOpts, companyLoading, companySource,
    onCompanySearch: searchCompanies,
    onCompanyChange: (val) => { setCompany(val); fetchAgreement(val); loadContacts(val); },
    onRefreshCompanies: refreshCompanies, companyRefreshTerm, companyInputRef,
    employees, accountManager, onAccountManagerChange: setAccountManager,
    contacts, contact, contactsLoading, onContactChange: setContact,
    contactZoekOpts, contactZoekLoading, onContactZoek: zoekContactOveral, mailContact,
    // Contact gekozen zonder klant: klant volgt uit de contactpersoon, daarna de
    // gewone keten (prijsafspraken + contactlijst; loadContacts bewaart de keuze).
    onContactMetKlant: (c) => {
      if (!c?.company?.id) return;
      const comp = { id: c.company.id, name: c.company.name };
      setCompany(comp); setContact({ id: c.id, name: c.name, email: c.email });
      setMailContact(null); fetchAgreement(comp); loadContacts(comp);
    },
    onReloadContacts: () => loadContacts(company),
    draft, onDraftField: (f, v) => setDraft((prev) => ({ ...prev, [f]: v })),
    language, onLanguageChange: (v) => setDraft((prev) => (prev ? { ...prev, language: v } : prev)),
    clientAgreement, englishGaps: createResult?.englishGaps || [],
  };

  const chatsColumnOpen = chatsOverride === null ? !(mode === 'new' && started) : chatsOverride;

  // Breedte van de gesprekken-kolom: versleepbaar, per gebruiker onthouden (localStorage).
  const [chatsBreedte, setChatsBreedte] = useState(() => leesGesprekkenBreedte(userEmail));
  useEffect(() => { setChatsBreedte(leesGesprekkenBreedte(userEmail)); }, [userEmail]);
  const sleepRef = useRef(null);
  const startSleep = (e) => {
    e.preventDefault();
    sleepRef.current = { x: e.clientX, w: chatsBreedte, laatst: chatsBreedte };
    const beweeg = (ev) => {
      if (!sleepRef.current) return;
      const w = begrensBreedte(sleepRef.current.w + ev.clientX - sleepRef.current.x);
      sleepRef.current.laatst = w;
      setChatsBreedte(w);
    };
    const los = () => {
      window.removeEventListener('mousemove', beweeg);
      window.removeEventListener('mouseup', los);
      document.body.style.cursor = ''; document.body.style.userSelect = '';
      if (sleepRef.current) bewaarGesprekkenBreedte(userEmail, sleepRef.current.laatst);
      sleepRef.current = null;
    };
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', beweeg);
    window.addEventListener('mouseup', los);
  };
  const zetBreedte = (w) => { const b = begrensBreedte(w); setChatsBreedte(b); bewaarGesprekkenBreedte(userEmail, b); };

  const gesprekkenPanel = (
    <>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 0.5, mb: 1.25 }}>
        <ChatBubbleOutlineIcon sx={{ fontSize: 15, color: ACCENT.dark }} />
        <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>
          Gesprekken
        </Typography>
        <Tooltip title="Gesprekken inklappen (meer ruimte voor de offerte)">
          <IconButton size="small" onClick={() => setChatsOverride(false)}
            sx={{ display: { xs: 'none', md: 'inline-flex' }, mr: -0.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#94a3b8' }}>◂</Typography>
          </IconButton>
        </Tooltip>
      </Stack>
      <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={() => { setMode('new'); newChat(); }}
        sx={{ mb: 1.25, bgcolor: ACCENT.main, textTransform: 'none', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: ACCENT.dark } }}>
        Nieuw gesprek
      </Button>
      {savedChats.length > 4 && (
        <TextField size="small" fullWidth placeholder="Zoek op titel, klant of offertenummer…" value={chatSearch}
          onChange={(e) => setChatSearch(e.target.value)} sx={{ mb: 1, '& .MuiInputBase-root': { bgcolor: '#fff', borderRadius: 2 } }} />
      )}
      <Typography sx={{ px: 0.5, mb: 0.5, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#94a3b8' }}>
        Eerdere gesprekken
      </Typography>
      {savedChats.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, display: 'block' }}>
          Nog geen gesprekken — worden automatisch bewaard.
        </Typography>
      ) : (
        <Stack spacing={0.25} sx={{ flex: { md: 1 }, minHeight: 0, maxHeight: { xs: 320, md: 'none' }, overflowY: 'auto' }}>
          {savedChats
            .filter(c => gesprekMatcht(c, chatSearch))
            .map((c) => {
              const activeC = currentChatId === c.id;
              const inGripp = c.status === 'in_gripp';
              const weergave = gesprekWeergave(c);
              return (
                <Stack key={c.id} direction="row" spacing={1} alignItems="flex-start" onClick={() => { setMode('new'); openChat(c); }}
                  sx={{ px: 1, py: 0.75, borderRadius: 1.5, cursor: 'pointer',
                    borderLeft: '3px solid', borderColor: activeC ? ACCENT.main : 'transparent',
                    bgcolor: activeC ? ACCENT.soft : 'transparent',
                    '& .chatVerwijder': { opacity: { xs: 1, md: 0 } },
                    '&:hover': { bgcolor: activeC ? ACCENT.soft : '#f3f4f6' },
                    '&:hover .chatVerwijder': { opacity: 1 } }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: 1, flexShrink: 0, display: 'grid', placeItems: 'center', mt: 0.25,
                    bgcolor: inGripp ? '#dcfce7' : ACCENT.soft, color: inGripp ? '#16a34a' : ACCENT.dark }}>
                    {inGripp ? <ReceiptLongIcon sx={{ fontSize: 15 }} /> : <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{weergave.volledig}</Box>}
                      placement="right" enterDelay={400}>
                      <Typography variant="body2" fontWeight={activeC ? 700 : 600}
                        sx={{ color: activeC ? ACCENT.dark : '#374151', lineHeight: 1.3, overflowWrap: 'anywhere',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {weergave.titel}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4, fontSize: 11, color: inGripp ? '#16a34a' : '#94a3b8' }}>
                      {inGripp ? '✓ in Gripp' : 'concept'}{c.updated_at ? ` · ${String(c.updated_at).slice(0, 10)}` : ''}
                    </Typography>
                  </Box>
                  {inGripp && c.gripp_offer_id && (
                    <Tooltip title="PDF downloaden">
                      <IconButton size="small" className="chatVerwijder" aria-label="PDF downloaden"
                        onClick={(e) => { e.stopPropagation(); downloadOffertePdf({ offerId: c.gripp_offer_id, subject: c.title }); }}
                        sx={{ flexShrink: 0, color: '#94a3b8', transition: 'opacity 0.15s', '&:hover': { color: ACCENT.dark } }}>
                        <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Gesprek verwijderen">
                    <IconButton size="small" className="chatVerwijder"
                      onClick={(e) => { e.stopPropagation(); verwijderChat(c); }}
                      sx={{ flexShrink: 0, color: '#94a3b8', transition: 'opacity 0.15s', '&:hover': { color: '#dc2626' } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            })}
        </Stack>
      )}
    </>
  );

  return (
    // Volle breedte: vaste gesprekken-kolom links (op de plek van de secundaire nav),
    // daarnaast de gecentreerde hoofdkolom met intake/concept.
    <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
      {chatsColumnOpen ? (
        <Box sx={{ width: chatsBreedte, flexShrink: 0, display: { xs: 'none', md: 'flex' },
          position: 'sticky', top: 0, alignSelf: 'flex-start', height: 'calc(100vh - 44px)',
          borderRight: '1px solid #eceff1', bgcolor: '#fafbfc' }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto', px: 1.5, py: 2 }}>
            {gesprekkenPanel}
          </Box>
          {/* Sleeprand: breedte aanpassen; dubbelklik = standaardbreedte. */}
          <Box role="separator" aria-orientation="vertical" aria-label="Breedte gesprekken aanpassen"
            aria-valuenow={chatsBreedte} aria-valuemin={GESPREKKEN_BREEDTE.min} aria-valuemax={GESPREKKEN_BREEDTE.max}
            tabIndex={0} title="Sleep om de breedte aan te passen (dubbelklik: standaard)"
            onMouseDown={startSleep} onDoubleClick={() => zetBreedte(GESPREKKEN_BREEDTE.standaard)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') { e.preventDefault(); zetBreedte(chatsBreedte - 20); }
              if (e.key === 'ArrowRight') { e.preventDefault(); zetBreedte(chatsBreedte + 20); }
            }}
            sx={{ position: 'absolute', top: 0, right: -3, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 2,
              '&:hover, &:focus-visible': { bgcolor: ACCENT.soft, outline: 'none' } }} />
        </Box>
      ) : (
        // Tijdens een lopend gesprek is de lijst met eerdere gesprekken niet meer nodig —
        // ingeklapt tot een smalle strip, één klik om 'm terug te halen.
        <Box onClick={() => setChatsOverride(true)} title="Gesprekken tonen"
          sx={{ width: 40, flexShrink: 0, display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
            alignItems: 'center', gap: 1, position: 'sticky', top: 0, alignSelf: 'flex-start',
            height: 'calc(100vh - 44px)', borderRight: '1px solid #eceff1', bgcolor: '#fafbfc', py: 2,
            cursor: 'pointer', '&:hover': { bgcolor: '#eef2ff' } }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#6366f1' }}>▸</Typography>
          <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: ACCENT.dark }} />
          <Typography sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 800,
            fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>
            Gesprekken
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2, sm: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
      {/* Split-view (chat + werk naast elkaar) heeft meer breedte nodig zodra het gesprek loopt. */}
      <Box sx={{ width: '100%', maxWidth: mode === 'new' && started ? 1800 : 1100, mx: 'auto' }}>
      <PageHeader
        icon="request_quote"
        title="Offerte Generator"
        subtitle="Start vanuit een e-mail of gespreksnotities — de assistent vraagt door over wat ontbreekt (verzint niets) en stelt het concept op."
        accent={{ bg: ACCENT.tile, color: ACCENT.dark }}
        rightActions={(
          <>
            <Button size="small" startIcon={<SettingsOutlinedIcon sx={{ fontSize: 16 }} />
}
              onClick={(e) => setBeheerAnchor(e.currentTarget)}
              sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b' }}>
              Beheer
            </Button>
            <Menu anchorEl={beheerAnchor} open={!!beheerAnchor} onClose={() => setBeheerAnchor(null)}>
              <MenuItem onClick={() => { setBeheerAnchor(null); openAudit(false); }}>
                <TranslateIcon sx={{ fontSize: 18, mr: 1, color: ACCENT.dark }} /> NL↔EN producten
              </MenuItem>
              <MenuItem onClick={() => { setBeheerAnchor(null); setBasisOpen(true); }}>
                <PlaylistAddCheckIcon sx={{ fontSize: 18, mr: 1, color: ACCENT.dark }} /> Basisoffertes (Gripp)
              </MenuItem>
              <MenuItem onClick={() => { setBeheerAnchor(null); setShowAfspraken(v => !v); }}>
                <HandshakeIcon sx={{ fontSize: 18, mr: 1, color: '#0e7490' }} /> Prijsafspraken beheren
              </MenuItem>
              {/* Feedback over de tool zelf. De feedback-assistent staat al app-breed
                  (FeedbackFAB); we geven 'm de route mee zodat de tool-vraag is
                  beantwoord en je meteen kunt vertellen wat er beter kan. */}
              <MenuItem onClick={() => {
                setBeheerAnchor(null);
                window.dispatchEvent(new CustomEvent('open-feedback', {
                  detail: { targetRepo: 'ff-material-app', route: '/offertes' },
                }));
              }}>
                <FeedbackOutlinedIcon sx={{ fontSize: 18, mr: 1, color: '#6366f1' }} /> Feedback over deze tool
              </MenuItem>
            </Menu>
          </>
        )}
      />
      {/* Zichtbaar dat hij bezig is (wiki: 'niet duidelijk dat hij bezig is met genereren'). */}
      {(chatLoading || linesLoading || creating || checkLoading) && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 5, mb: 1 }}>
          <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
          <Typography variant="caption" sx={{ color: '#6d28d9', fontWeight: 700 }}>
            {creating ? 'Concept-offerte wordt in Gripp aangemaakt…' : linesLoading ? 'Offerteregels worden opgebouwd…' : checkLoading ? 'Offerte wordt gecontroleerd…' : 'De assistent schrijft aan de offerte… dit kan een halve minuut duren.'}
          </Typography>
        </Box>
      )}

      {/* Eén offerte-kiezer: nieuw óf bestaand — al het werk gebeurt in hetzelfde
          document. De losse AI-check op een Gripp-offerte zit achter "Alleen
          controleren" in de open-dialoog (en blijft via ?mode=check bereikbaar);
          drie concurrerende mode-knoppen waren er twee te veel. */}
      {mode !== 'check' && (
        <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
          <Button variant={editingOffer ? 'outlined' : 'contained'} startIcon={<DescriptionIcon sx={{ fontSize: 18 }} />}
            onClick={() => { newChat(); setMode('new'); }}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: editingOffer ? undefined : ACCENT.main, '&:hover': editingOffer ? {} : { bgcolor: ACCENT.dark } }}>
            Nieuwe offerte
          </Button>
          <Button variant={editingOffer ? 'contained' : 'outlined'} startIcon={<EditNoteIcon sx={{ fontSize: 19 }} />}
            onClick={() => { setMode('new'); setLoadDlg({ q: '', loading: false }); }}
            sx={{ textTransform: 'none', fontWeight: 700, ...(editingOffer ? { bgcolor: ACCENT.main, '&:hover': { bgcolor: ACCENT.dark } } : {}) }}>
            {editingOffer ? `Bewerkt: offerte ${editingOffer.number}` : 'Bestaande offerte…'}
          </Button>
        </Stack>
      )}

      {/* Losse AI-offertecheck op een bestaande Gripp-offerte. */}
      {mode === 'check' && (
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ border: '1px solid #fde68a', borderRadius: 2, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.1, bgcolor: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: '#d97706', color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <FactCheckIcon sx={{ fontSize: 19 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#92400e', lineHeight: 1.2 }}>Bestaande offerte controleren</Typography>
                <Typography variant="caption" color="text.secondary">Kies een offerte uit Gripp — AI checkt logica, huistarief, BTW, bron, spelling en vergelijkt met de vorige offerte.</Typography>
              </Box>
              <Button size="small" onClick={() => setMode('new')}
                sx={{ textTransform: 'none', fontWeight: 700, color: '#92400e', flexShrink: 0 }}>
                ← Terug naar de generator
              </Button>
            </Stack>
            <Box sx={{ p: 2 }}>
              <Autocomplete
                size="small" options={refOpts} value={checkOffer}
                loading={refLoading} filterOptions={(x) => x}
                getOptionLabel={(o) => (o ? `${o.number} — ${o.subject}` : '')}
                isOptionEqualToValue={(o, v) => o.number === v.number}
                onInputChange={(_e, val, reason) => { if (reason === 'input') searchOffers(val); }}
                onChange={(_e, val) => setCheckOffer(val)}
                renderOption={(props, o) => (
                  <Box component="li" {...props} key={o.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{o.number} — {o.subject}</Typography>
                      <Typography variant="caption" color="text.secondary">{[o.company, o.date].filter(Boolean).join(' · ')}</Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => <TextField {...params} label="Offerte zoeken (nummer, klant of onderwerp)" placeholder="Bijv. 9647 of klantnaam…" />}
                noOptionsText="Typ om te zoeken"
              />
              {/* Meest recente offertes — snel selecteren zonder zoeken. */}
              {recentOffers.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em' }}>MEEST RECENT</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                    {recentOffers.map((o) => (
                      <Chip key={o.id} size="small" clickable onClick={() => setCheckOffer(o)}
                        label={`${o.number} — ${o.subject}`}
                        variant={checkOffer?.number === o.number ? 'filled' : 'outlined'}
                        sx={{ maxWidth: 260, borderColor: '#fde68a',
                          bgcolor: checkOffer?.number === o.number ? '#fef3c7' : '#fff',
                          color: '#92400e', '& .MuiChip-label': { fontWeight: 600 } }} />
                    ))}
                  </Stack>
                </Box>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                <Button variant="contained" sx={{ bgcolor: '#d97706', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#b45309' } }}
                  startIcon={checkLoading ? <CircularProgress size={16} color="inherit" /> : <FactCheckIcon />}
                  onClick={runCheckExisting} disabled={checkLoading || !checkOffer}>
                  {checkLoading ? 'Controleren…' : 'Controleer offerte'}
                </Button>
                {checkOffer && (
                  <Button variant="outlined" startIcon={<PictureAsPdfIcon fontSize="small" />}
                    onClick={() => downloadOffertePdf({ offerNumber: checkOffer.number, offerId: checkOffer.id, subject: checkOffer.subject })}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>PDF downloaden</Button>
                )}
                {checkOffer && (
                  <PresentatieKnop offerNumber={checkOffer.number} senderName={accountManager?.name || ''}
                    companyName={checkOffer.company || ''} />
                )}
                {checkOffer && buildGrippOfferUrl(checkOffer.id) && (
                  <Tooltip title="Opent de offerte direct in Gripp (je moet in Gripp ingelogd zijn).">
                    <Button variant="outlined" startIcon={<OpenInNewIcon fontSize="small" />}
                      href={buildGrippOfferUrl(checkOffer.id)} target="_blank" rel="noopener"
                      sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT.dark, borderColor: '#c7d2fe',
                        '&:hover': { borderColor: ACCENT.main, bgcolor: ACCENT.soft } }}>
                      Open in Gripp
                    </Button>
                  </Tooltip>
                )}
              </Stack>
              <CheckProgress active={checkLoading} />
              {checkResult && <CheckResultView result={checkResult} />}

              {/* Vervolg-chat: doorpraten over de gecontroleerde offerte (2-weg). */}
              {checkResult && checkOffer && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: ACCENT.dark }} />
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#334155' }}>Doorvragen over deze offerte</Typography>
                  </Stack>
                  {checkMessages.length > 0 && (
                    <Box sx={{ maxHeight: 320, overflowY: 'auto', mb: 1, pr: 0.5 }}>
                      {checkMessages.map((m, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', mb: 1 }}>
                          <Paper elevation={0} sx={{ px: 1.5, py: 1, maxWidth: '85%',
                            bgcolor: m.role === 'user' ? ACCENT.main : '#f1f5f9',
                            color: m.role === 'user' ? '#fff' : '#1f2937', borderRadius: 2, whiteSpace: 'pre-wrap' }}>
                            <Typography variant="body2">{linkifyText(m.content)}</Typography>
                          </Paper>
                        </Box>
                      ))}
                      {checkChatLoading && <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}><CircularProgress size={14} /><Typography variant="caption">Assistent zoekt het uit…</Typography></Stack>}
                    </Box>
                  )}
                  {checkMessages.length === 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
                      {['Zijn er eerdere offertes op deze klant?', 'Vergelijk met een soortgelijke offerte', 'Wat mist er nog aan standaardregels?'].map((s) => (
                        <Chip key={s} size="small" clickable label={s} onClick={() => sendCheckChat(s)}
                          sx={{ bgcolor: ACCENT.soft, color: ACCENT.dark, '& .MuiChip-label': { fontWeight: 600 } }} />
                      ))}
                    </Stack>
                  )}
                  <Stack direction="row" spacing={1} alignItems="flex-end">
                    <TextField fullWidth size="small" multiline maxRows={4} placeholder="Stel een vraag over deze offerte…"
                      value={checkChatInput} onChange={(e) => setCheckChatInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendCheckChat(); } }} />
                    <Button variant="contained" onClick={() => sendCheckChat()} disabled={checkChatLoading || !checkChatInput.trim()}
                      sx={{ minWidth: 0, px: 1.5, bgcolor: ACCENT.main, '&:hover': { bgcolor: ACCENT.dark } }}><SendIcon fontSize="small" /></Button>
                  </Stack>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {mode === 'new' && (
      /* Hoofdkolom. Vóór de start smal/gecentreerd (rustige intake); zodra het gesprek
         loopt de volle breedte zodat chat + werk lekker breed naast elkaar passen. */
      <Box sx={{ maxWidth: started ? 'none' : 900, mx: 'auto', width: '100%' }}>

      {showAfspraken && <Box sx={{ mb: 2 }}><KlantAfspraken userEmail={userEmail} /></Box>}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Vanuit een project geopend: laten zien wát er is meegekomen. Zonder deze
          balk lijkt de generator blanco te starten terwijl de context wél in het
          bron-veld staat — en zie je ook niet dat het project zelf leeg is. */}
      {!started && flowProjectId && flowContext && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, borderColor: '#c7d2fe', bgcolor: '#eef2ff' }}>
          {flowContext.laden && <Typography variant="body2" sx={{ color: '#4338ca' }}>Projectcontext laden…</Typography>}
          {flowContext.fout && (
            <Typography variant="body2" sx={{ color: '#b45309' }}>
              Projectcontext kon niet geladen worden ({flowContext.fout}) — je kunt gewoon zelf een bron plakken.
            </Typography>
          )}
          {flowContext.project && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#3730a3' }}>
                Vanuit project {flowContext.project.number ? `${flowContext.project.number} · ` : ''}{flowContext.project.name}
                {flowContext.project.client ? ` — ${flowContext.project.client}` : ''}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}>
                <Chip size="small" label={flowContext.briefingVelden
                  ? `Briefing: ${flowContext.briefingVelden} velden` : 'Geen briefing in dit project'}
                  color={flowContext.briefingVelden ? 'success' : 'default'} variant="outlined" />
                <Chip size="small" label={flowContext.paspoortVelden
                  ? `Klantpaspoort: ${flowContext.paspoortVelden} velden` : 'Geen klantpaspoort'}
                  color={flowContext.paspoortVelden ? 'success' : 'default'} variant="outlined" />
                <Chip size="small" label={flowContext.klantGekoppeld
                  ? 'Klant gekoppeld aan Gripp' : 'Klant nog kiezen'}
                  color={flowContext.klantGekoppeld ? 'success' : 'warning'} variant="outlined" />
              </Stack>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#4c1d95' }}>
                Deze gegevens staan al in het bron-veld hieronder. Vul aan met de e-mail of gespreksnotities.
              </Typography>
            </>
          )}
        </Paper>
      )}

      {/* Bron + start — na het starten ook oproepbaar als 'Achtergrond' bij een (bestaande) offerte */}
      {(!started || achtergrondOpen) && (
        <SectionCard title={started ? 'Achtergrond bij deze offerte' : 'Bron'} icon={<MailOutlineIcon sx={{ fontSize: 19 }} />}
          subtitle={started
            ? 'E-mails, notulen of documenten als context — de assistent gebruikt ze bij het aanpassen (bv. "vul de omschrijving in op basis van de achtergrond")'
            : 'Kies een meeting, doorzoek notulen & e-mail in één veld, of plak notities'}>
          <Autocomplete
            size="small" options={meetings}
            getOptionLabel={(m) => m?.title || m?.subject || m?.name || (m?.date ? `Meeting ${String(m.date).slice(0, 10)}` : `Meeting ${m?.id}`)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_e, val) => pickMeeting(val)} loading={loadingMeeting}
            renderInput={(params) => <TextField {...params} label="Kies een meeting (vult het bron-veld)" placeholder="Zoek een meeting…" />}
            noOptionsText="Geen meetings gevonden"
          />

          <Divider sx={{ my: 1.25 }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>of zoek</Typography>
          </Divider>

          {/* Eén gecombineerd zoekveld: doorzoekt OneNote-notulen ÉN je e-mail. */}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" fullWidth label="Zoek in meetings, notulen & e-mail (klantnaam of onderwerp)"
                value={srcQuery} onChange={(e) => setSrcQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSourceSearch(); } }} />
              <Button size="small" variant="outlined" onClick={runSourceSearch} disabled={srcLoading || !srcQuery.trim()}>
                {srcLoading ? <CircularProgress size={16} /> : 'Zoek'}
              </Button>
            </Stack>
            {srcError && <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>{srcError}</Typography>}
            {srcResults && !srcResults.length && !srcError && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Niets gevonden in notulen of e-mail.</Typography>
            )}
            {srcResults && srcResults.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1, maxHeight: 260, overflowY: 'auto' }}>
                {srcResults.flatMap((it) => {
                  // Een gesprek is één rij ("+ hele gesprek"); de losse treffers
                  // eruit staan ingesprongen eronder zodra je ze uitklapt.
                  const rij = (item, ml = 0) => {
                    const SRC = item.kind === 'thread'
                      ? { label: 'Gesprek', bg: '#e0f2fe', color: '#0369a1' }
                      : ({
                        onenote: { label: 'OneNote', bg: '#ede9fe', color: '#6d28d9' },
                        email: { label: 'E-mail', bg: '#e0f2fe', color: '#0369a1' },
                        meeting: { label: 'Meeting', bg: '#dcfce7', color: '#15803d' },
                      }[item.source] || { label: 'Bron', bg: '#f1f5f9', color: '#475569' });
                    return (
                      <Stack key={item.id} direction="row" spacing={1} alignItems="center"
                        sx={{ bgcolor: '#fff', border: '1px solid #eee', borderRadius: 1, px: 1, py: 0.5, ml }}>
                        <Chip size="small" label={SRC.label}
                          sx={{ flexShrink: 0, height: 20, fontSize: 10, fontWeight: 700, bgcolor: SRC.bg, color: SRC.color }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>{item.title}</Typography>
                          {item.sub && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{item.sub}</Typography>}
                        </Box>
                        {item.kind === 'thread' && (
                          <Typography variant="caption"
                            onClick={() => setOpenGesprekken(v => ({ ...v, [item.id]: !v[item.id] }))}
                            sx={{ color: '#64748b', cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}
                            title="De losse berichten uit dit gesprek die op je zoekterm matchten">
                            {openGesprekken[item.id] ? 'verberg berichten' : `berichten (${item.kids.length})`}
                          </Typography>
                        )}
                        {item.source !== 'meeting' && (
                          <Button size="small" onClick={() => openBronPreview(item)}
                            sx={{ minWidth: 0, px: 0.75, textTransform: 'none', fontSize: 12 }} title="Inhoud bekijken zonder toe te voegen">
                            👁 preview
                          </Button>
                        )}
                        {item.source === 'meeting'
                          ? <Link href={`/MeetingDashboard/${item.refId}`} target="_blank" rel="noopener noreferrer" variant="caption">notulen</Link>
                          : item.url && <Link href={item.url} target="_blank" rel="noopener noreferrer" variant="caption">openen</Link>}
                        <Button size="small" onClick={() => addSourceResult(item)} disabled={srcAdding === item.id} sx={{ flexShrink: 0 }}>
                          {srcAdding === item.id ? <CircularProgress size={14} />
                            : (item.kind === 'thread' ? '+ hele gesprek' : '+ toevoegen')}
                        </Button>
                      </Stack>
                    );
                  };
                  const rijen = [rij(it)];
                  if (it.kind === 'thread' && openGesprekken[it.id]) {
                    it.kids.forEach(kid => rijen.push(rij(kid, 3)));
                  }
                  return rijen;
                })}
              </Stack>
            )}
            {srcResults && (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                Zie je een meeting niet? Er verschijnen alleen meetings waar jij host/deelnemer van was. Nieuwe opname net gehad? Klik nog eens op <b>Zoek</b> om te verversen.
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 1.25 }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>of plak zelf</Typography>
          </Divider>

          <Box sx={{ position: 'relative' }}>
            <TextField fullWidth multiline minRows={4} maxRows={14} size="small"
              placeholder={luistert ? 'Ik luister… spreek de briefing in' : 'Plak hier de e-mail of gespreksnotities… of spreek hem in met de microfoon'}
              value={mail} onChange={(e) => setMail(e.target.value)}
              sx={{ '& textarea': { fontSize: 13, lineHeight: 1.7, paddingRight: '40px' } }} />
            {/* Inspreken i.p.v. typen (wiki Offerte generator): spraakherkenning van de browser,
                werkt op de laptop in Chrome en Edge; tekst komt achter wat er al staat. */}
            {spraakBeschikbaar && (
              <Tooltip title={luistert ? 'Stop met luisteren' : 'Briefing inspreken (Chrome/Edge)'} arrow>
                <IconButton size="small" onClick={luistert ? stopLuisteren : startLuisteren}
                  sx={{ position: 'absolute', right: 6, top: 6, color: luistert ? '#dc2626' : '#6d28d9', bgcolor: luistert ? '#fee2e2' : 'transparent', '&:hover': { bgcolor: luistert ? '#fecaca' : '#ede9fe' } }}>
                  {luistert ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
            <input ref={attachInputRef} type="file" hidden multiple accept=".pdf,.docx,.pptx,.txt,.md"
              onChange={(e) => addAttachments([...e.target.files])} />
            <Button variant="text" size="small" disabled={attachBusy}
              onClick={() => attachInputRef.current?.click()}
              startIcon={attachBusy ? <CircularProgress size={14} /> : <DescriptionIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 700, color: '#0e7490' }}>
              {attachBusy ? 'Bijlage lezen…' : '📎 Bijlage toevoegen (PDF/Word/PowerPoint) — concept of briefing als bron'}
            </Button>
            {mail.trim() && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {mail.trim().length.toLocaleString('nl-NL')} tekens bron-materiaal toegevoegd
              </Typography>
            )}
          </Stack>

          {/* Gekoppelde bronnen — onthouden bij deze offerte (ook na heropenen). */}
          {sources.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                📎 Gekoppelde bronnen ({sources.length}) — bewaard bij deze offerte
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                {sources.map((s) => {
                  const meta = {
                    meeting: { label: 'Meeting', bg: '#dcfce7', color: '#15803d' },
                    onenote: { label: 'OneNote', bg: '#ede9fe', color: '#6d28d9' },
                    email: { label: 'E-mail', bg: '#dbeafe', color: '#1d4ed8' },
                    bijlage: { label: 'Bijlage', bg: '#fef3c7', color: '#b45309' },
                  }[s.kind] || { label: 'Bron', bg: '#f1f5f9', color: '#475569' };
                  return (
                    <Chip key={s.id} size="small" onDelete={() => removeTrackedSource(s.id)}
                      label={`${meta.label}: ${s.title}${s.sub ? ` · ${s.sub}` : ''}`}
                      sx={{ maxWidth: 320, bgcolor: meta.bg, color: meta.color, fontWeight: 600, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                  );
                })}
              </Stack>
            </Box>
          )}
          {started && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="small" disabled={!String(mail || '').trim() || chatLoading} onClick={vulOmschrijvingMetAchtergrond}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT.main, '&:hover': { bgcolor: ACCENT.dark } }}>
                ✍️ Omschrijving invullen op basis van deze achtergrond
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Of stel je eigen vraag in de chat — de achtergrond gaat automatisch mee.
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => setAchtergrondOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Sluiten</Button>
            </Stack>
          )}
        </SectionCard>
      )}
      {!started && (
        <>

        {/* Klant & referentie zijn optioneel: de assistent herkent de klant meestal zelf
            uit de bron. Standaard ingeklapt zodat de intake rustig blijft (Bron → Start). */}
        <Button variant="text" onClick={() => setShowKlantRef(v => !v)}
          startIcon={<BusinessIcon sx={{ fontSize: 18 }} />}
          sx={{ mb: showKlantRef ? 1 : 1, textTransform: 'none', fontWeight: 700, color: '#0e7490' }}>
          {showKlantRef ? 'Verberg klant & referentie' : 'Klant, prijsafspraken & oude offertes (optioneel)'}
        </Button>
        {showKlantRef && (
        <SectionCard title="Klant & referentie" icon={<BusinessIcon sx={{ fontSize: 19 }} />}
          subtitle="Alleen als je wilt sturen — de assistent herkent de klant meestal zelf uit de bron."
          color="#0891b2" softBg="#ecfeff">
          <Autocomplete
            sx={{ maxWidth: 420 }} size="small" options={companyOpts} value={company}
            getOptionLabel={(o) => o?.name || ''} isOptionEqualToValue={(o, v) => o.id === v.id}
            loading={companyLoading} filterOptions={(x) => x}
            onInputChange={(_e, val, reason) => { if (reason === 'input') searchCompanies(val); }}
            onChange={(_e, val) => { setCompany(val); fetchAgreement(val); loadContacts(val); }}
            renderInput={(params) => <TextField {...params} label="Klant (optioneel — laadt prijsafspraken)" placeholder="Zoek bedrijf in Gripp…" />}
            noOptionsText="Typ om te zoeken"
          />
          {companySource === 'spiegel' && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Gripp is even niet bereikbaar — deze klanten komen uit onze eigen kopie en
              kunnen achterlopen. Voorbereiden kan gewoon; naar Gripp sturen pas als de
              verbinding er weer is.
            </Alert>
          )}
          {clientAgreement && (
            <Alert severity="success" icon={<HandshakeIcon fontSize="inherit" />} sx={{ mt: 1 }}>
              Prijsafspraken voor <b>{clientAgreement.company_name}</b> worden meegenomen.
            </Alert>
          )}

          {/* Oude offertes als referentie — de assistent mag hieruit lengtes/formaten/prijzen afleiden. */}
          <Autocomplete
            sx={{ mt: 1.5 }} size="small" options={refOpts} value={null} blurOnSelect clearOnBlur
            loading={refLoading} filterOptions={(x) => x}
            getOptionLabel={(o) => `${o.number} — ${o.subject}`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onInputChange={(_e, val, reason) => { if (reason === 'input') searchOffers(val); }}
            onChange={(_e, val) => { if (val) addReference(val); }}
            renderOption={(props, o) => (
              <Box component="li" {...props} key={o.id}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{o.number} — {o.subject}</Typography>
                  <Typography variant="caption" color="text.secondary">{[o.company, o.contact, o.date].filter(Boolean).join(' · ')}{o.hasDescription ? '' : ' · (geen omschrijving)'}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="Oude offertes zoeken (referentie/vergelijken)" placeholder="Zoek op klant, contactpersoon of onderwerp…" />}
            noOptionsText="Typ om te zoeken"
          />
          {references.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
              {references.map(r => (
                <Chip key={r.number} size="small"
                  label={r.id ? (
                    <a href={buildGrippOfferUrl(r.id)} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                      title="Open deze offerte in Gripp om te vergelijken">
                      {r.number} — {r.subject} ↗
                    </a>
                  ) : `${r.number} — ${r.subject}`}
                  onDelete={() => removeReference(r.number)}
                  sx={{ bgcolor: '#eef2ff', color: '#4338ca', maxWidth: 340 }} />
              ))}
            </Stack>
          )}
        </SectionCard>
        )}

        <Button fullWidth size="large" variant="contained" startIcon={<AutoAwesomeIcon />} onClick={startChat}
          sx={{ bgcolor: ACCENT.main, py: 1.25, textTransform: 'none', fontWeight: 800, fontSize: '1rem',
            boxShadow: '0 4px 14px rgba(79,70,229,.35)', '&:hover': { bgcolor: ACCENT.dark } }}>
          Start gesprek met de assistent
        </Button>
        </>
      )}

      {/* Split-view: links het (doorlopende) gesprek, rechts het werk (concept/regels/
          controle/Gripp). Zo blijf je chatten terwijl je de offerte ziet en bewerkt. */}
      {/* Documentweergave: de offerte als document in het midden, de assistent als
          balk onderin, de Gripp-velden in een eigenschappen-paneel rechts. */}
      {started && (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 1040, mx: 'auto' }}>
          <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Box sx={{ p: { xs: 2, md: 3 }, pb: 1.5 }}>
              {draft && (
                <OfferteStatusRegel
                  action={flowAction}
                  linesCount={(draft.lines || []).length}
                  total={total}
                  onAction={handleFlowAction}
                  onBadgeClick={handleBadgeClick}
                  extraActions={(
                    <>
                      <Tooltip title="E-mails, notulen of documenten toevoegen als context voor het aanpassen van deze offerte">
                        <Button size="small" onClick={() => setAchtergrondOpen((v) => !v)}
                          sx={{ textTransform: 'none', fontWeight: 700, color: achtergrondOpen ? ACCENT.main : '#475569' }}>
                          📎 Achtergrond{sources.length ? ` (${sources.length})` : ''}
                        </Button>
                      </Tooltip>
                      {!widePropsPanel && (
                        <Button size="small" onClick={() => setPropsDrawerOpen(true)}
                          sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>
                          Eigenschappen{eigenschappenMissing > 0 ? ` ⚠ ${eigenschappenMissing}` : ''}
                        </Button>
                      )}
                      <OfferteEmailButton
                        subject={draft.subject} description={draft.description}
                        companyName={company?.name || ''} contactName={contact?.name || ''}
                        contactEmail={contact?.email || ''}
                        offerNumber={createResult?.offerNumber || null} source={mail}
                        senderName={accountManager?.name || userEmail} />
                      {createResult?.ok && createResult.offerNumber && (
                        <Tooltip title="Kopieer de link die deze offerte direct in deze interne tool opent — handig om te delen met collega's.">
                          <Button size="small" onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(`${window.location.origin}/offertes?mode=new&number=${createResult.offerNumber}`);
                              toast.success('Interne link gekopieerd');
                            } catch { toast.error('Kopiëren geblokkeerd door de browser — kopieer de URL handmatig'); }
                          }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>
                            🔗 Interne link
                          </Button>
                        </Tooltip>
                      )}
                      {createResult?.ok && (createResult.offerNumber || createResult.offerId) && (
                        <Button size="small" startIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
                          onClick={() => downloadOffertePdf({ offerNumber: createResult.offerNumber, offerId: createResult.offerId, subject: draft.subject })}
                          sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>PDF downloaden</Button>
                      )}
                      {createResult?.ok && createResult.offerNumber && (
                        <PresentatieKnop size="small" offerNumber={createResult.offerNumber} source={mail}
                          senderName={accountManager?.name || ''} companyName={company?.name || ''}
                          sx={{ color: '#475569' }} />
                      )}
                    </>
                  )}
                />
              )}
              {createResult?.error && (
                <Alert severity="error" sx={{ mb: 2 }}>Aanmaken in Gripp mislukt: {createResult.error}</Alert>
              )}
              {createResult?.ok && createResult.updateError && (
                <Alert severity="warning" sx={{ mb: 2 }}>Laatste bijwerken in Gripp mislukte: {createResult.updateError}</Alert>
              )}

              {/* AI-offertecheck: voortgang en bevindingen, boven het document */}
              {checkLoading && <CheckProgress active={checkLoading} />}
              {checkResult && !checkLoading && (
                <Box sx={{ mb: 2, border: '1px solid #fde68a', borderRadius: 2, p: 2, bgcolor: '#fffefb' }}>
                  <CheckResultView result={checkResult} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 2 }}>
                    <Button variant="contained" size="small" onClick={applyCheckFindings}
                      disabled={chatLoading}
                      startIcon={chatLoading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
                      sx={{ bgcolor: '#d97706', textTransform: 'none', fontWeight: 800, '&:hover': { bgcolor: '#b45309' } }}>
                      {chatLoading ? 'Assistent verwerkt…' : 'Verwerk bevindingen in het concept'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      …of pas het document zelf aan en draai de check daarna opnieuw.
                    </Typography>
                  </Stack>
                </Box>
              )}

              {draft ? (
                <>
                {/* Meerdere offertes in één omschrijving? Bied de splitsing aan. */}
                {gesplitsteOffertes && offerteWachtrij.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}
                    action={<Button size="small" variant="contained" onClick={splitsNu} sx={{ textTransform: 'none', fontWeight: 700 }}>✂️ Splits in {gesplitsteOffertes.length} offertes</Button>}>
                    Deze omschrijving bevat {gesplitsteOffertes.length} offertes — naar Gripp gaat er altijd één per keer.
                    Splitsen zet offerte 1 in het document en de rest in een wachtrij.
                  </Alert>
                )}
                {offerteWachtrij.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}
                    action={<Button size="small" variant="outlined" onClick={volgendeUitWachtrij} sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Volgende klaarzetten →
                    </Button>}>
                    Nog {offerteWachtrij.length} offerte{offerteWachtrij.length === 1 ? '' : 's'} in de wachtrij
                    ({offerteWachtrij.map((w) => `"${w.subject}"`).join(', ')}). Rond deze eerst af (regels + Gripp), zet dan de volgende klaar.
                  </Alert>
                )}
                <OfferteDocument
                  draft={draft}
                  onSubjectChange={(v) => setDraft((prev) => ({ ...prev, subject: v }))}
                  onDescriptionChange={(v) => setDraft((prev) => ({ ...prev, description: v }))}
                  setLines={setLines} catalogus={basisCatalogus} onLoadCatalogus={loadBasisOffers}
                  standaardBtw={company?.vat ?? null}
                  sortLinesBySection={sortLinesBySection}
                  genLines={genLines} linesLoading={linesLoading}
                  priceCheck={priceCheck} products={products} total={total}
                  basisOffers={basisOffers} onLoadBasisOffers={loadBasisOffers} onStartFromBasis={startFromBasis}
                />
                {/* Zelfde actie als de knop bovenin — na het doorlopen van de
                    regels wil je niet terugscrollen (verzoek 20 aug). */}
                {(draft?.lines || []).length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    {createResult?.ok && buildGrippOfferUrl(createResult.offerId, company?.id) && (
                      <Button variant="outlined" href={buildGrippOfferUrl(createResult.offerId, company?.id)}
                        target="_blank" rel="noopener"
                        sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT.dark, borderColor: '#c7d2fe',
                          '&:hover': { borderColor: ACCENT.main, bgcolor: ACCENT.soft } }}>
                        Open in Gripp
                      </Button>
                    )}
                    <Button variant="contained" onClick={() => setPreflightOpen(true)} disabled={creating}
                      startIcon={creating ? <CircularProgress size={15} color="inherit" /> : null}
                      sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT.main, '&:hover': { bgcolor: ACCENT.dark } }}>
                      {createResult?.ok
                        ? `Werk ${editingOffer ? `offerte ${editingOffer.number}` : 'de offerte'} bij in Gripp`
                        : 'Maak concept-offerte in Gripp'}
                    </Button>
                  </Box>
                )}
                </>
              ) : (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    De assistent stelt eerst een paar vragen — beantwoord ze hieronder, dan verschijnt hier het concept.
                  </Typography>
                </Box>
              )}
            </Box>
            {/* Sticky balk — directe child van het Paper (anders breekt sticky) */}
            <AssistentBalk
              messages={messages} prefill={chatPrefill} invoerRef={chatInvoerRef}
              onSend={onSend} chatLoading={chatLoading}
              vragen={draft?.vragen || []} notes={draft?.notes || ''}
              references={references} onRemoveReference={removeReference}
              refOpts={refOpts} refLoading={refLoading} onSearchOffers={searchOffers} onAddReference={addReference}
              showForceDraft={!draft} onForceDraft={forceDraft}
              threadOpen={threadOpen || !draft} onThreadToggle={() => setThreadOpen((v) => !v)}
              canUndo={undoCount > 0} onUndo={undoDraft}
              scope={scopeLock} onScopeChange={(v) => { setScopeLock(v); scopeRef.current = v; }}
              versieCount={versies.length} onVersies={() => setVersiesOpen(true)}
            />
            <VersiesDrawer open={versiesOpen} onClose={() => setVersiesOpen(false)} versies={versies} huidig={draft} onHerstel={herstelVersie} />
          </Paper>
        </Box>

        {/* Eigenschappen: persistent paneel op breed scherm, anders een Drawer */}
        {draft && (widePropsPanel ? (
          <Box sx={{ position: 'sticky', top: 12, alignSelf: 'flex-start' }}>
            <OfferteEigenschappen variant="panel" missingCount={eigenschappenMissing} {...eigenschappenProps} />
          </Box>
        ) : (
          <OfferteEigenschappen variant="drawer" open={propsDrawerOpen}
            onClose={() => setPropsDrawerOpen(false)}
            missingCount={eigenschappenMissing} {...eigenschappenProps} />
        ))}

        <OffertePreflight
          open={preflightOpen}
          onClose={() => setPreflightOpen(false)}
          onConfirm={() => { setPreflightOpen(false); createInGripp(); }}
          actionLabel={createResult?.ok
            ? `Werk ${editingOffer ? `offerte ${editingOffer.number}` : 'de offerte'} bij in Gripp`
            : 'Maak concept-offerte in Gripp'}
          busy={creating}
          company={company} isExistingOffer={isExistingOffer} editingOffer={editingOffer}
          linesCount={(draft?.lines || []).length} total={total}
          missingFields={grippFieldsMissing} klantMissing={klantMissing}
          priceCheckWarnings={priceCheckWarnings} priceCheck={priceCheck} lines={draft?.lines || []}
          englishGaps={(createResult?.englishGaps || []).length} language={language}
          checkResult={checkResult} onRunCheck={runCheck} checking={checkLoading}
        />
      </Box>
      )}

      </Box>
      )}

      {/* Mobiel: gesprekkenlijst onder de wizard (desktop heeft de vaste kolom links). */}
      {mode === 'new' && (
        <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 3, maxWidth: 900, mx: 'auto' }}>{gesprekkenPanel}</Box>
      )}

      {/* Bestaande offerte openen → in de split-view laden en via chat aanpassen. */}
      {/* Bron-preview: mail/notulen inkijken zonder toe te voegen (zoals bij Frits) */}
      <Dialog open={!!srcPreview?.open} onClose={() => setSrcPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pr: 5 }}>{srcPreview?.title || 'Preview'}</DialogTitle>
        <DialogContent dividers>
          {srcPreview?.sub && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{srcPreview.sub}</Typography>
          )}
          {srcPreview?.loading
            ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
            : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{renderBronTekst(srcPreview?.body)}</Typography>}
        </DialogContent>
        <DialogActions>
          {srcPreview?.url && <Link href={srcPreview.url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ mr: 'auto', ml: 1 }}>In Outlook openen ↗</Link>}
          <Button onClick={() => { const it = srcPreview?.item; setSrcPreview(null); if (it) addSourceResult(it); }} variant="contained" sx={{ textTransform: 'none' }}>+ Toevoegen</Button>
          <Button onClick={() => setSrcPreview(null)} sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!loadDlg} onClose={() => loadDlg?.loading || setLoadDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Bestaande offerte openen</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Zoek op <b>nummer, klant of onderwerp</b> — of vul het offertenummer in (bv. 9643). De offerte opent
            als document; aanpassen doe je via de assistent-balk onderin (bijv. "zet alle deliverables van
            seconden naar minuten").
          </Typography>
          <Autocomplete
            freeSolo size="small" options={refOpts} value={null} loading={refLoading} filterOptions={(x) => x}
            disabled={loadDlg?.loading} blurOnSelect clearOnBlur={false}
            getOptionLabel={(o) => (typeof o === 'string' ? o : `${o.number} — ${o.subject || ''}`)}
            isOptionEqualToValue={(o, v) => o.number === v.number}
            onInputChange={(_e, val, reason) => { if (reason === 'input') { setLoadDlg((d) => ({ ...(d || {}), q: val })); searchOffers(val); } }}
            onChange={(_e, val) => { if (val && typeof val === 'object' && val.number) loadExistingOffer(val.number); }}
            renderOption={(props, o) => (
              <Box component="li" {...props} key={o.id || o.number}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{o.number} — {o.subject}</Typography>
                  <Typography variant="caption" color="text.secondary">{[o.company, o.contact, o.date].filter(Boolean).join(' · ')}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} autoFocus label="Zoek offerte of vul nummer in" placeholder="nummer, klant of onderwerp…"
                onKeyDown={(e) => { if (e.key === 'Enter' && /^\s*\d+\s*$/.test(loadDlg?.q || '')) { e.preventDefault(); loadExistingOffer(loadDlg.q); } }} />
            )} />
          {loadDlg?.error && <Alert severity="error" sx={{ mt: 1.5 }}>{loadDlg.error}</Alert>}
        </DialogContent>
        <DialogActions>
          {/* Niet bewerken maar alleen de AI-check draaien — de losse check-flow
              (vergelijkt ook met de vorige offerte, met doorvraag-chat). */}
          <Button onClick={() => { setLoadDlg(null); setMode('check'); }} disabled={loadDlg?.loading}
            startIcon={<FactCheckIcon sx={{ fontSize: 16 }} />}
            sx={{ mr: 'auto', textTransform: 'none', fontWeight: 700, color: '#b45309' }}>
            Alleen controleren
          </Button>
          <Button onClick={() => setLoadDlg(null)} disabled={loadDlg?.loading} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={() => loadExistingOffer(loadDlg.q)} disabled={loadDlg?.loading || !loadDlg?.q?.trim()}
            startIcon={loadDlg?.loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            {loadDlg?.loading ? 'Laden…' : 'Openen'}
          </Button>
        </DialogActions>
      </Dialog>

      <BasisoffertesDialog open={basisOpen} onClose={() => { setBasisOpen(false); setBasisOffers(null); }} />

      {/* NL↔EN product-audit (Fase 1): AI-voorstel + stale-flags. Read-only. */}
      <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { height: '86vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <TranslateIcon sx={{ color: ACCENT.dark }} />
          <Typography variant="h6" component="span">NL↔EN producten — koppeling & controle</Typography>
          {audit && <Chip size="small" label={`${audit.pairCount} paren · ${audit.flagged} met aandachtspunt`} sx={{ bgcolor: ACCENT.soft, color: ACCENT.dark }} />}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => openAudit(true)} disabled={auditLoading} sx={{ textTransform: 'none' }}>Opnieuw analyseren</Button>
            <MuiIconButton size="small" onClick={() => setAuditOpen(false)}><CloseIcon fontSize="small" /></MuiIconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {auditLoading && (
            <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Catalogus ophalen en NL↔EN-paren voorstellen… (de eerste keer tot ~1 min; daarna gecached)</Typography>
            </Stack>
          )}
          {!auditLoading && auditError && (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
              <Alert severity="error" sx={{ width: '100%' }}>{auditError}</Alert>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Mogelijk is de backend-deploy nog bezig (dit is een nieuwe functie) — probeer het zo nog eens.
              </Typography>
              <Button variant="outlined" onClick={() => openAudit(true)}>Opnieuw proberen</Button>
            </Stack>
          )}
          {!auditLoading && !auditError && !audit && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Nog niets geladen.</Typography>
          )}
          {/* Stap 1: cureren — vink foute paren uit, kies het juiste tarief. */}
          {!auditLoading && !auditError && audit && cleanupStep === 'review' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Vink foute paren uit en zet het juiste tarief (standaard = NL-tarief). 🟠 = tarief wijkt af, 🔵 = EN-omschrijving ontbreekt.
                Er wordt <b>nog niets</b> gewijzigd — je krijgt eerst een voorstel te zien.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Nederlands</TableCell>
                    <TableCell>Engels</TableCell>
                    <TableCell align="right">NL €</TableCell>
                    <TableCell align="right">EN € (nu)</TableCell>
                    <TableCell align="right">Nieuw €</TableCell>
                    <TableCell>Aandacht</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audit.pairs.map((p, i) => {
                    const on = !!approved[p.en.id];
                    return (
                      <TableRow key={i} sx={{ opacity: on ? 1 : 0.5, bgcolor: p.flags.length && on ? '#fffbeb' : undefined }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={on} onChange={(e) => setApproved(a => ({ ...a, [p.en.id]: e.target.checked }))} />
                        </TableCell>
                        <TableCell>{p.nl.name}{!p.nl.hasDescription && <Typography component="span" variant="caption" color="text.disabled"> · geen omschr.</Typography>}</TableCell>
                        <TableCell>{p.en.name}{!p.en.hasDescription && <Typography component="span" variant="caption" sx={{ color: '#2563eb' }}> · geen omschr.</Typography>}</TableCell>
                        <TableCell align="right">{p.nl.price != null ? `€${p.nl.price}` : '—'}</TableCell>
                        <TableCell align="right" sx={p.flags.includes('rate_mismatch') ? { color: '#d97706', fontWeight: 700 } : {}}>
                          {p.en.price != null ? `€${p.en.price}` : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <TextField variant="standard" size="small" type="number" value={targetPrices[p.en.id] ?? ''}
                            disabled={!on} onChange={(e) => setTargetPrices(t => ({ ...t, [p.en.id]: e.target.value }))}
                            sx={{ width: 72, '& input': { textAlign: 'right' } }} />
                        </TableCell>
                        <TableCell>
                          {p.flags.includes('rate_mismatch') && <Chip size="small" label="tarief" sx={{ mr: 0.5, bgcolor: '#fef3c7', color: '#92400e' }} />}
                          {p.flags.includes('en_missing_description') && <Chip size="small" label="EN omschr." sx={{ bgcolor: '#dbeafe', color: '#1e40af' }} />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {!audit.pairs.length && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Geen duidelijke NL↔EN-paren gevonden.</Typography>}
            </>
          )}

          {/* Stap 2: dry-run voorstel + doorvoeren. */}
          {!auditLoading && cleanupStep === 'preview' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Dit zou er in Gripp wijzigen. Controleer en klik dan op <b>Doorvoeren in Gripp</b>. Alleen de gemarkeerde velden veranderen.
              </Typography>
              {applyResults && (
                <Alert severity={applyResults.failed ? 'warning' : 'success'} sx={{ mb: 2 }}>
                  {applyResults.applied} bijgewerkt in Gripp{applyResults.failed ? `, ${applyResults.failed} mislukt` : ''}.
                </Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Engels product</TableCell>
                    <TableCell align="right">Tarief</TableCell>
                    <TableCell>Omschrijving (EN)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(cleanupPreview || []).map((c, i) => (
                    <TableRow key={i}>
                      <TableCell>{c.en_name}</TableCell>
                      <TableCell align="right">
                        {c.price?.changed
                          ? <span><Box component="span" sx={{ color: '#94a3b8', textDecoration: 'line-through' }}>{c.price.from != null ? `€${c.price.from}` : '—'}</Box> → <b style={{ color: '#d97706' }}>€{c.price.to}</b></span>
                          : <Box component="span" sx={{ color: '#94a3b8' }}>{c.price?.to != null ? `€${c.price.to}` : '—'} (ongewijzigd)</Box>}
                      </TableCell>
                      <TableCell>
                        {c.description?.changed
                          ? <Typography variant="caption" sx={{ color: '#1e40af' }}>+ {c.description.to}</Typography>
                          : <Typography variant="caption" color="text.disabled">{c.description?.from ? '(al aanwezig)' : '(geen)'}</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!(cleanupPreview || []).length && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Geen wijzigingen.</Typography>}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          {cleanupStep === 'review' && audit && !auditLoading && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
                {Object.values(approved).filter(Boolean).length} van {audit.pairCount} goedgekeurd
              </Typography>
              <Button variant="contained" onClick={runCleanupPreview} disabled={cleanupBusy}
                startIcon={cleanupBusy ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ bgcolor: ACCENT.main, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: ACCENT.dark } }}>
                {cleanupBusy ? 'Voorstel maken…' : 'Genereer opschoon-voorstel (dry-run)'}
              </Button>
            </>
          )}
          {cleanupStep === 'preview' && (
            <>
              <Button onClick={() => { setCleanupStep('review'); setApplyResults(null); }} sx={{ mr: 'auto', textTransform: 'none' }}>← Terug</Button>
              {!applyResults && (
                <Button variant="contained" onClick={applyCleanup} disabled={cleanupBusy}
                  startIcon={cleanupBusy ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ bgcolor: '#059669', textTransform: 'none', fontWeight: 800, '&:hover': { bgcolor: '#047857' } }}>
                  {cleanupBusy ? 'Bezig…' : 'Doorvoeren in Gripp'}
                </Button>
              )}
              {applyResults && <Button variant="contained" onClick={() => openAudit(true)} sx={{ textTransform: 'none', bgcolor: ACCENT.main }}>Ververs audit</Button>}
            </>
          )}
        </DialogActions>
      </Dialog>
      </Box>
      </Box>
    </Box>
  );
}
